import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import HeaderLogged from './components/header_logged';
import api from './api/client';
import './YOYForecastPage.css';
import { MARKETPLACE_CONFIGS } from './configs/marketplaceConfigs'; 
import MarketplaceTabs from './yoy-components/MarketplaceTabs';
import YoySummaryTable from './yoy-components/YoySummaryTable';

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { 
  faPenToSquare, 
  faUpload, 
  faArrowsRotate, 
  faFilter,
  faAngleDown,
  faAngleUp,
  faDownload,
  faFile,
  faSeedling,
  faPlus,
  faMinus
} from '@fortawesome/free-solid-svg-icons';
import { faPenToSquare as farPenToSquare } from '@fortawesome/free-regular-svg-icons';

// Components
import YoyAdminPanel from './yoy-components/YoyAdminPanel';
import YoyEditPopup from './yoy-components/YoyEditPopup';
import YoyAmzStockPopup from './yoy-components/YoyAmzStockPopup';

// Utils
import {
  calculateDaysSupplied,
  calculateDueDate,
  getDueDateClass,
  getWeeksSuppliedClass,
  getForecastCellClass,
  calculateFBAStockAfterWeeks,
  calculateDaysSuppliedAtWeek,
  calculateDaysSuppliedFromWeek,
  getWeeksBetween,
  calculateStockAfterWeeks,
  calculateColorByWeeks,
  getExtendedForecastWeeks,
  renderDaysSuppliedCell,
  calculateStockRemainingAfterWeeks,
   
} from './utils/yoyCalculations';

import {
  calculatePercentageChange,
  calculateAverageGrowth,
  calculateForecast,
  getWeekNumber,
  getDateOfISOWeek,
  getCurrentWeekAndYear,
  getHistoricalWeeks,
  getForecastWeeks,
} from './utils/yoyPredictionCalculations';

import YoySummaryMonthlyTable from './yoy-components/YoySummaryMonthlyTable';
import YoyMonthlyView from './yoy-components/YoyMonthlyView';
import {
  getCurrentMonthInfo,
  getHistoricalMonths,
  getForecastMonths,
  buildMonthlyYearRanges,
  buildMonthlyQueryRange,
  calculateDaysSuppliedMonthly,
  getMonthlyDaysSuppliedTooltip,
  buildMonthlyConsumptionLog,
  buildMonthlySalesIndex,
  calculateMonthlyAverageGrowthFromIndex,
  calculateMonthlyForecastFromIndex,
  calculateMonthlySeasonalityForecast,
} from './utils/yoyMonthlyCalculations';

library.add(
  faPenToSquare, faUpload, faArrowsRotate, faFilter,
  faAngleDown, faAngleUp, faDownload, faFile, faSeedling, farPenToSquare,
  faPlus, faMinus
);



const DAYS_SUPPLIED_CALCULATION_WEEKS = 52;

// Месяц плана производства ({ year, month_num } из monthlyDisplayMonths) позже текущего?
const isFuturePlanMonth = (planMonth, curInfo) =>
  planMonth.year > curInfo.year || (planMonth.year === curInfo.year && planMonth.month_num > curInfo.month);

// Ключи данных агента (сервер отдаёт недели с ведущим нулём: '2026-W05')
const aiWeekKey = (y, w) => `${y}-W${String(w).padStart(2, '0')}`;

// Совпадение прогноза с фактом, % (итог обсуждения 07.09.2026):
// 100% = точное попадание; шкала = меньшее/большее, поэтому симметрична
// (20 против 5 и 5 против 20 дают одинаковые 25%) и не схлопывается в 0
// при промахе >2×, как старая формула с делением на факт.
const forecastMatchPct = (fc, actual) => {
  if (typeof fc !== 'number' || typeof actual !== 'number') return null;
  if (fc === 0 && actual === 0) return 100;
  const hi = Math.max(fc, actual);
  if (hi <= 0) return null;
  return Math.round((Math.min(fc, actual) / hi) * 100);
};

// Отклонение прогноза от факта со знаком (просьба 23.09.2026): −50% = прогноз
// ниже факта на 50% (занижен, недопланировали), +100% = прогноз вдвое выше
// факта (завышен). actual=0 при прогнозе >0 → Infinity (показ капится).
const forecastDeviationPct = (fc, actual) => {
  if (typeof fc !== 'number' || typeof actual !== 'number') return null;
  if (fc === actual) return 0;
  if (actual <= 0) return fc > 0 ? Infinity : null;
  return Math.round(((fc - actual) / actual) * 100);
};
const fmtDeviation = (d) =>
  d == null ? null : `${d > 0 ? '+' : ''}${Math.min(d, 999)}%`;
// Мелкий подписанный % отклонения рядом с точностью (0 не показываем —
// точное попадание и так видно по 100%)
const renderDeviation = (dev) => (dev == null || dev === 0) ? null : (
  <span className={`hist-dev ${dev < 0 ? 'dev-under' : 'dev-over'}`}>{fmtDeviation(dev)}</span>
);

// Значение агента в ячейке строки AI Forecast: основное значение + прирост %
// к прошлому году (как в основной строке) + мелкий % точности + кружок «?».
// Тултип «?» по наведению И попап по клику (onInfo) — у менеджера hover-тултип
// не срабатывает.
const renderAiValue = (rec, prevValue, onInfo) => {
  if (!rec || typeof rec.value !== 'number') return '—';
  const tip = [];
  if (rec.p10 != null) tip.push(`P10 (conservative) — ${rec.p10}`);
  if (rec.p50 != null) tip.push(`P50 (median) — ${rec.p50}`);
  if (rec.p90 != null) tip.push(`P90 (optimistic) — ${rec.p90}`);
  if (rec.accuracy != null) tip.push(`Self-assessed accuracy — ${rec.accuracy}%`);
  let change = null;
  if (typeof prevValue === 'number' && prevValue > 0) {
    const pct = Math.round(((rec.value - prevValue) / prevValue) * 100);
    change = `${pct >= 0 ? '+' : ''}${pct}%`;
  }
  const rows = [];
  if (rec.p10 != null) rows.push({ label: 'P10 (conservative)', value: rec.p10.toLocaleString() });
  if (rec.p50 != null) rows.push({ label: 'P50 (median)', value: rec.p50.toLocaleString() });
  if (rec.p90 != null) rows.push({ label: 'P90 (optimistic)', value: rec.p90.toLocaleString() });
  if (rec.accuracy != null) rows.push({ label: 'Self-assessed accuracy', value: `${rec.accuracy}%` });
  if (change) rows.push({ label: 'Change vs last year', value: change, accent: true });
  return (
    <span className="ai-value-wrap">
      {rec.value.toLocaleString()}
      {change && (
        <span className={`change-indicator ${change.startsWith('+') ? 'positive' : 'negative'}`}>{change}</span>
      )}
      {/* accuracy в ячейке не показываем (просьба менеджера) — только в тултипе/попапе */}
      {tip.length > 0 && (
        <span
          className="ai-info-dot"
          title={tip.join('\n')}
          onClick={onInfo ? (e) => { e.stopPropagation(); onInfo({ title: 'AI Forecast Details', rows }); } : undefined}
        >?</span>
      )}
    </span>
  );
};

// ── Дилы Amazon (ведёт ИИ-агент, /api/yoy-deals) ────────────────────────────
// Кружочек рядом с продажами периода, пересекающегося с дилом; тултип —
// даты дила, для завершённых ещё и сколько продано за дил.
const isoWeekStartDate = (year, week) => {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7) + (week - 1) * 7);
  return monday;
};
const isoWeekRangeStr = (year, week) => {
  const start = isoWeekStartDate(year, week);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
};
const dealsForRange = (deals, start, end) =>
  (deals || []).filter(d => d.start_date <= end && d.end_date >= start);
const fmtDealDate = (s) =>
  new Date(`${s}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const dealLines = (deals) => deals.map(d => {
  const range = `${fmtDealDate(d.start_date)} – ${fmtDealDate(d.end_date)}`;
  const label = d.type || 'Deal';
  if (d.status === 'completed') {
    return `${label}: ${range}${d.units_sold != null ? ` — ${d.units_sold} units sold during the deal` : ' (completed)'}`;
  }
  return `${label}: scheduled for ${range}`;
});
// Тултип по наведению И попап по клику (onInfo, карточки дилов)
const renderDealDot = (deals, onInfo) => {
  if (!deals || !deals.length) return null;
  const lines = dealLines(deals);
  return (
    <span
      className="deal-dot"
      title={lines.join('\n')}
      onClick={onInfo ? (e) => { e.stopPropagation(); onInfo({ title: 'Amazon Deals', deals }); } : undefined}
    />
  );
};

function YOYForecastPage() {
  // --------------------------------------------------------------
  // MARKETPLACE STATE
  // --------------------------------------------------------------
  const [activeMarketplace, setActiveMarketplace] = useState('amazon');
  // 'summary' is a virtual tab — fall back to amazon config so identifierField is always defined
  const marketplaceConfig = MARKETPLACE_CONFIGS[activeMarketplace] ?? MARKETPLACE_CONFIGS['amazon'];
  const identifierField = marketplaceConfig.identifierField; // 'asin' or 'sku'

  // --------------------------------------------------------------
  // DATA STORES PER MARKETPLACE
  // --------------------------------------------------------------
  const [salesDataByMarketplace, setSalesDataByMarketplace] = useState({});
  const [monthlySalesDataByMarketplace, setMonthlySalesDataByMarketplace] = useState({});
  const [stockDataByMarketplace, setStockDataByMarketplace] = useState({}); // for future use
  const [daysSuppliedDataByMarketplace, setDaysSuppliedDataByMarketplace] = useState({});

  // --------------------------------------------------------------
  // SHARED PRODUCTS (loaded once from backend)
  // --------------------------------------------------------------
  const [productsData, setProductsData] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  // --------------------------------------------------------------
  // GLOBAL UI STATE (weeks, filters, expansions, etc.)
  // --------------------------------------------------------------
  const [expandedRows, setExpandedRows] = useState({});
  const [showAllPreviousYears, setShowAllPreviousYears] = useState(true);
  const [visibleColumns, setVisibleColumns] = useState({
    historical: true,
    currentWeek: true,
    prediction: true
  });
  const [searchQuery, setSearchQuery] = useState('');
  // showFilters/showUploadMenu переехали в YoyAdminPanel (31.08.2026):
  // здесь каждый клик по тумблеру перерисовывал всю страницу с таблицей

  // Week configuration
  const [currentWeekInfo, setCurrentWeekInfo] = useState(null);
  const [historicalWeeks, setHistoricalWeeks] = useState([]);
  const [forecastWeeks, setForecastWeeks] = useState([]);
  const [historicalWeeksCount, setHistoricalWeeksCount] = useState(4);
  const [forecastWeeksCount, setForecastWeeksCount] = useState(52);
  const [customHistoricalWeeks, setCustomHistoricalWeeks] = useState('');
  const [customForecastWeeks, setCustomForecastWeeks] = useState('');
  const [medianWeeksCount, setMedianWeeksCount] = useState(4);

  // Loading state for sales data
  const [loadingSales, setLoadingSales] = useState(false);

  // --------------------------------------------------------------
  // FILTERS (shared across marketplaces)
  // --------------------------------------------------------------
  const [brandsData, setBrandsData] = useState([]);
  const [nichesData, setNichesData] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedNiches, setSelectedNiches] = useState([]);
  const [selectedProductTypes, setSelectedProductTypes] = useState([]);

  // --------------------------------------------------------------
  // SEASONALITY (only for Amazon)
  // --------------------------------------------------------------
  const [seasonalityFlags, setSeasonalityFlags] = useState({});
  const [seasonalityCoefficients, setSeasonalityCoefficients] = useState([]);
  const seasonalityForecastCache = useRef({});
  // Кеш для стандартного YoY-прогноза (не сезонного).
  // Очищается при каждой загрузке данных продаж.
  const yoyForecastCache = useRef({});

  // --------------------------------------------------------------
  // WALMART
  // --------------------------------------------------------------
  const [walmartStockData, setWalmartStockData] = useState({});

  // --------------------------------------------------------------
  // PLAN TO PRODUCE (masterbags_dailyplan)
  // { SKU: totalQtyFromTodayToLastPlanDate }
  // --------------------------------------------------------------
  const [planToProduceData, setPlanToProduceData] = useState({});

  // --------------------------------------------------------------
  // MONTHLY PLAN TO PRODUCE STATE
  // monthlyPlanData: { SKU: { 'YYYY-MM': qty, ... }, ... }
  // monthlyPlanMeta: { months: [{ key: 'YYYY-MM', year, month_num, label }] }
  //   с текущего месяца, через границу года (план на январь не теряется)
  // monthlyColumnsExpanded: boolean — показывать ли раскрытые месячные столбцы
  // --------------------------------------------------------------
  const [monthlyPlanData, setMonthlyPlanData] = useState({});
  const [monthlyPlanMeta, setMonthlyPlanMeta] = useState({ months: [] });
  const [monthlyColumnsExpanded, setMonthlyColumnsExpanded] = useState(false);


  // --------------------------------------------------------------
  // VIEW MODE: 'weekly' | 'monthly'
  // --------------------------------------------------------------
  const [viewMode, setViewMode] = useState('weekly');

  // Количество исторических/прогнозных месяцев (аналогично неделям)
  const [historicalMonthsCount, setHistoricalMonthsCount] = useState(() => {
    try { const v = localStorage.getItem('yoy_historical_months_count'); return v ? Number(v) : 2; } catch { return 2; }
  });
  const [forecastMonthsCount,   setForecastMonthsCount]   = useState(12);
  const [medianMonthsCount,     setMedianMonthsCount]     = useState(2);

  // Persist historicalMonthsCount so ShipmentsPlanningPage forecast hook can read it
  useEffect(() => {
    try { localStorage.setItem('yoy_historical_months_count', String(historicalMonthsCount)); } catch {}
  }, [historicalMonthsCount]);

  // Load AI agent forecast + page forecast snapshots for the Agentic row
  useEffect(() => {
    ['week', 'month'].forEach(period => {
      api.get(`/api/yoy-ai-forecast?period=${period}`)
        .then(res => {
          if (res.data?.success) {
            setAiForecastData(prev => ({ ...prev, [period]: { ai: res.data.ai, page: res.data.page } }));
          }
        })
        .catch(err => console.error(`Error loading AI forecast (${period}):`, err));
    });
    // Дилы Amazon (ведёт ИИ-агент): { ASIN: [{start_date, end_date, type,
    // status, units_sold}] } — кружочек у продаж пересекающихся периодов
    api.get('/api/yoy-deals')
      .then(res => { if (res.data?.success) setDealsData(res.data.deals || {}); })
      .catch(err => console.error('Error loading deals:', err));
    // рекомендованный агентом Plan to Produce: { ASIN: qty }
    api.get('/api/yoy-ai-plan')
      .then(res => { if (res.data?.success) setAiPlanData(res.data.plan || {}); })
      .catch(err => console.error('Error loading AI plan:', err));
  }, []);

  // --------------------------------------------------------------
  // WEEKLY PLAN TO PRODUCE (masterbags_dailyplan grouped by ISO week)
  // weeklyPlanData: { SKU: { 'YYYY-Www': qty, ... }, ... }
  // --------------------------------------------------------------
  const [weeklyPlanData, setWeeklyPlanData] = useState({});

  // Toggle: show/hide Stock Balance Row (hidden by default)
  const [showStockBalanceRow, setShowStockBalanceRow] = useState(false);
  const toggleStockBalanceRow = () => setShowStockBalanceRow(prev => !prev);

  // --------------------------------------------------------------
  // AGENTIC (AI FORECAST) ROW — Amazon only
  // aiForecastData: { week|month: { ai: {asin: {'2026-W05': v}}, page: {…} } }
  // 'ai' — прогноз ИИ-агента, 'page' — снапшоты нашего прогноза (для прошедших
  // периодов сервер отдаёт последнюю версию, созданную до конца периода).
  // --------------------------------------------------------------
  const [showAgenticRow, setShowAgenticRow] = useState(() => {
    try { return localStorage.getItem('yoy_show_agentic_row') === '1'; } catch { return false; }
  });
  const toggleAgenticRow = () => setShowAgenticRow(prev => {
    try { localStorage.setItem('yoy_show_agentic_row', prev ? '0' : '1'); } catch {}
    return !prev;
  });
  const [aiForecastData, setAiForecastData] = useState({ week: null, month: null });
  const [dealsData, setDealsData] = useState({});
  const [aiPlanData, setAiPlanData] = useState({}); // { ASIN: qty } — план от агента
  // История версий ИИ-прогноза + откат (версия = минута отправки агента)
  const [aiHistory, setAiHistory] = useState({ open: false, period: 'week', versions: [], loading: false, busy: false });

  const reloadAiForecast = (period) => {
    api.get(`/api/yoy-ai-forecast?period=${period}`)
      .then(res => {
        if (res.data?.success) {
          setAiForecastData(prev => ({ ...prev, [period]: { ai: res.data.ai, page: res.data.page } }));
        }
      })
      .catch(err => console.error(`Error reloading AI forecast (${period}):`, err));
  };

  const loadAiVersions = (period) => {
    setAiHistory(h => ({ ...h, period, loading: true }));
    api.get(`/api/yoy-ai-versions?period=${period}`)
      .then(res => setAiHistory(h => ({ ...h, versions: res.data?.versions || [], loading: false })))
      .catch(() => setAiHistory(h => ({ ...h, versions: [], loading: false })));
  };

  const rollbackAiForecast = (version) => {
    const period = aiHistory.period;
    if (!window.confirm(
      `Roll the AI ${period === 'week' ? 'weekly' : 'monthly'} forecast back to the version of ${version}?\n\n` +
      'Current and future periods will be restored to that state (a new version is written — history is kept). ' +
      'Past periods and periods that only exist in newer versions are not touched.'
    )) return;
    setAiHistory(h => ({ ...h, busy: true }));
    api.post('/api/yoy-ai-rollback', { period, to: version })
      .then(res => {
        alert(`Restored ${res.data?.restored ?? '?'} value(s) from ${version}.`);
        loadAiVersions(period);
        reloadAiForecast(period);
      })
      .catch(err => alert(err.response?.data?.error || 'Rollback failed'))
      .finally(() => setAiHistory(h => ({ ...h, busy: false })));
  };
  // Мини-попап по клику на «?» (перцентили ИИ) или кружочек дила —
  // hover-тултипы у менеджера не срабатывают
  const [infoPopup, setInfoPopup] = useState(null); // { title, lines }

  // Список месяцев считается на фронте — не зависит от ответа сервера.
  // Если сервер вернул months — используем их, иначе строим сами (текущий месяц..декабрь).
  const MONTH_LABELS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const monthlyDisplayMonths = useMemo(() => {
    if (monthlyPlanMeta.months && monthlyPlanMeta.months.length > 0) {
      return monthlyPlanMeta.months;
    }
    const now  = new Date();
    const year = now.getFullYear();
    const months = [];
    for (let m = now.getMonth() + 1; m <= 12; m++) {
      months.push({ key: `${year}-${String(m).padStart(2, '0')}`, year, month_num: m, label: MONTH_LABELS_SHORT[m - 1] });
    }
    return months;
  }, [monthlyPlanMeta.months]);
  // --------------------------------------------------------------
  // EDIT POPUP STATE
  // --------------------------------------------------------------
  const [editPopup, setEditPopup] = useState({
    isOpen: false,
    product: null,
    week: null,
    year: null,
    currentValue: '',
    field: null,
    showSuccessMessage: false,
    showErrorMessage: null,
    bestSeller: false,
    lightningDeal: false,
    newProduct: false,
    outOfStock: false
  });
  const [loadingEdit, setLoadingEdit] = useState(false);

  // --------------------------------------------------------------
  // UPLOAD STATES (Sales upload – per marketplace)
  // --------------------------------------------------------------
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(null);
  const fileInputRef = useRef(null);

  // --------------------------------------------------------------
  // STOCK UPLOAD (only for Amazon)
  // --------------------------------------------------------------
  const [showStockUploadPopup, setShowStockUploadPopup] = useState(false);
  const [selectedStockFile, setSelectedStockFile] = useState(null);
  const [parsedStockData, setParsedStockData] = useState([]);
  const [uploadStockStatus, setUploadStockStatus] = useState(null);
  const stockFileInputRef = useRef(null);
  const [awdStockData, setAwdStockData] = useState({}); // only for Amazon

  // --------------------------------------------------------------
  // HELPER: active marketplace sales data
  // --------------------------------------------------------------
  const activeSalesData = useMemo(() => {
    return salesDataByMarketplace[activeMarketplace] || {};
  }, [salesDataByMarketplace, activeMarketplace]);

  // --------------------------------------------------------------
  // HELPER: active marketplace days supplied data
  // --------------------------------------------------------------
  const activeDaysSuppliedData = useMemo(() => {
    return daysSuppliedDataByMarketplace[activeMarketplace] || {};
  }, [daysSuppliedDataByMarketplace, activeMarketplace]);

  // --------------------------------------------------------------
  // DATA ACCESS FUNCTIONS (use the active marketplace)
  // --------------------------------------------------------------
  const getSalesData = useCallback((identifier, year, week) => {
    if (!identifier) return 'N/A';
    const key = `${identifier}-${year}-W${week}`;
    const record = activeSalesData[key];
    return record?.value ?? 'N/A';
  }, [activeSalesData]);

  const getSalesDataFull = useCallback((identifier, year, week) => {
    if (!identifier) return null;
    const key = `${identifier}-${year}-W${week}`;
    return activeSalesData[key] || null;
  }, [activeSalesData]);

  // --------------------------------------------------------------
  // EFFECT: Load products and brands/niches on mount
  // --------------------------------------------------------------
  useEffect(() => {
    loadProducts();
    loadBrandsAndNiches();
  }, []);

  // --------------------------------------------------------------
  // EFFECT: When products are ready and marketplace changes, load sales
  // --------------------------------------------------------------
useEffect(() => {
  if (productsData.length > 0 && !loadingProducts) {
    if (activeMarketplace === 'summary') {
      // При открытии Summary — загрузить все маркетплейсы
      ['amazon', 'walmart', 'shopify', 'etsy'].forEach(mp => {
        if (!salesDataByMarketplace[mp]) {
          loadSalesDataForMarketplace(mp);
        }
      });
    } else if (!salesDataByMarketplace[activeMarketplace]) {
      // Кеш (31.08.2026): возврат на уже открытую вкладку не перекачивает
      // данные заново — раньше каждое переключение = запрос + каскад тяжёлых
      // перерисовок. Свежесть — Refresh All Data и загрузчики попапов.
      loadSalesDataForMarketplace(activeMarketplace);
    }
  }
}, [productsData, loadingProducts, activeMarketplace]);

  // План производства от маркетплейса не зависит — грузим один раз на продукты,
  // а не на каждое переключение вкладки
  useEffect(() => {
    if (productsData.length > 0 && !loadingProducts) {
      loadPlanToProduceData(productsData);
      loadMonthlyPlanData(productsData);
      loadWeeklyPlanData(productsData);
    }
  }, [productsData, loadingProducts]);

  // Load monthly sales data when switching to monthly view or when marketplace changes
  useEffect(() => {
    if (productsData.length > 0 && !loadingProducts && viewMode === 'monthly') {
      if (activeMarketplace === 'summary') {
        ['amazon', 'walmart', 'shopify', 'etsy'].forEach(mp => {
          if (!monthlySalesDataByMarketplace[mp]) loadMonthlySalesDataForMarketplace(mp);
        });
      } else if (!monthlySalesDataByMarketplace[activeMarketplace]) {
        // тот же кеш, что и у недельных данных
        loadMonthlySalesDataForMarketplace(activeMarketplace);
      }
    }
  }, [productsData, loadingProducts, viewMode, activeMarketplace]);

  // --------------------------------------------------------------
  // EFFECT: Expand all rows by default when products are loaded
  // --------------------------------------------------------------
  useEffect(() => {
    if (productsData.length > 0 && showAllPreviousYears) {
      const allExpanded = {};
      productsData.forEach(product => {
        allExpanded[product.id] = true;
      });
      setExpandedRows(allExpanded);
    }
  }, [productsData]);

  // --------------------------------------------------------------
  // EFFECT: When sales data or current week info changes, update days supplied
  // --------------------------------------------------------------
  useEffect(() => {
    if (productsData.length > 0 && !loadingProducts && currentWeekInfo) {
      updateDaysSuppliedDataForMarketplace(activeMarketplace);
    }
  // NOTE: activeSalesData intentionally excluded — updateDaysSuppliedDataForMarketplace
  // is already called at the end of loadSalesDataForMarketplace after data is fully loaded.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsData, loadingProducts, currentWeekInfo, activeMarketplace]);

  // --------------------------------------------------------------
  // EFFECT: Load seasonality coefficients (only once)
  // --------------------------------------------------------------
  useEffect(() => {
    const fetchSeasonalityCoefficients = async () => {
      try {
        const { data: result } = await api.get(`/api/seasonality-coefficients?marketplace=all`);
        if (result.success) {
          setSeasonalityCoefficients(result.data);
        }
      } catch (error) {
        console.error('Error fetching seasonality coefficients:', error);
      }
    };
    fetchSeasonalityCoefficients();
  }, []);

  // --------------------------------------------------------------
  // EFFECT: Load seasonality flags for all markets
  // --------------------------------------------------------------
  useEffect(() => {
    if (productsData.length > 0 && !loadingProducts) {
      const asins = productsData.map(p => p.asin).filter(Boolean);
      loadSeasonalityFlags(asins);
    }
  }, [productsData, loadingProducts]);

  // --------------------------------------------------------------
  // PRODUCT LOADING FUNCTIONS (unchanged from original, but we keep them)
  // --------------------------------------------------------------
  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      console.log('Loading products from database...');

      // Try main endpoint first
      const response = await api.get(`/api/all-products-wh-stock`);
      if (response.data) {
        const result = response.data;
        if (result.success && Array.isArray(result.data)) {
          const awdMap = await loadAWDStockMap();
          const fbaStockResult = await api.get(`/api/fba-stock`)
            .then(r => r.data)
            .catch(e => { if (!e.response) throw e; return { success: false, data: [] }; });

          const stockMap = new Map();
          if (fbaStockResult.success && Array.isArray(fbaStockResult.data)) {
            fbaStockResult.data.forEach(stockItem => {
              if (stockItem.ASIN) {
                stockMap.set(stockItem.ASIN, {
                  available: stockItem.available || 0,
                  inbound_quantity: stockItem.inbound_quantity || 0,
                  reserved_fc_transfer: stockItem.reserved_fc_transfer || 0,
                  reserved_fc_processing: stockItem.reserved_fc_processing || 0,
                  total_quantity: stockItem.total_quantity || 0
                });
              }
            });
          }

          const allProducts = result.data.map((item, index) => {
            const stockData = stockMap.get(item.ASIN);
            const awdStock = awdMap[item.ASIN?.trim()?.toUpperCase()] || 0;
            return {
              id: `${item.source}-${index}`,
              productName: `${item.brandID} - ${item.productName}`,
              asin: item.ASIN,
              sku: item.SKU,
              bagsPerBox: item.bagsPerBox || 0,
              whStock: item.whStock || 0,
              fbaStock: stockData ? stockData.total_quantity : 0,
              awdStock: awdStock,
              stockDetails: stockData || null,
              source: item.source,
              brandID: item.brandID,
              niche_category_id: item.niche_category_id || null
            };
          });

          setProductsData(allProducts);
          console.log(`Loaded ${allProducts.length} products via /api/all-products-wh-stock`);
          return;
        }
      }

      // Fallback: load from individual endpoints
      await loadProductsFromIndividualEndpoints();

    } catch (error) {
      console.error('Error loading products from main endpoint:', error);
      try {
        await loadProductsFromIndividualEndpoints();
      } catch (individualError) {
        console.error('Error loading from individual endpoints:', individualError);
        setProductsData(getExampleProducts());
      }
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadProductsFromIndividualEndpoints = async () => {
  try {
    const [
      setsData,
      wildmicrData,
      individualPacketsData,
      kitsData,
      fertData,
      readyData,
      fbaStockData,
      awdStockData,
      setsWhStockData,
      wildmicrWhStockData,
      individualPacketsWhStockData
    ] = await Promise.all([
      api.get(`/api/yoy-sets`).then(res => res.data),
      api.get(`/api/yoy-wildmicr`).then(res => res.data),
      api.get(`/api/yoy-individualpackets`).then(res => res.data),
      api.get(`/api/yoy-kits`).then(res => res.data).catch(() => ({ success: false, data: [] })),
      api.get(`/api/yoy-fert-products`).then(res => res.data).catch(() => ({ success: false, data: [] })),
      api.get(`/api/yoy-ready-products`).then(res => res.data).catch(() => ({ success: false, data: [] })),
      api.get(`/api/fba-stock`).then(res => res.data),
      api.get(`/api/awd-data?limit=1000`).then(res => res.data),
      api.get(`/api/wh-stock-sets`).then(res => res.data),
      api.get(`/api/wh-stock-wildmicr`).then(res => res.data),
      api.get(`/api/wh-stock-individualpackets`).then(res => res.data)
    ]);

    // ---------- Walmart stock ----------
    let walmartStockMap = new Map();
    try {
      const walmartStockResult = (await api.get(MARKETPLACE_CONFIGS.walmart.endpoints.stock)).data;
      if (walmartStockResult.success && Array.isArray(walmartStockResult.data)) {
        walmartStockResult.data.forEach(item => {
          if (item.sku) {
            walmartStockMap.set(item.sku.trim(), parseInt(item.stock) || 0);
          }
        });
      }
    } catch (error) {
      console.error('Error loading Walmart stock:', error);
    }

    let allProducts = [];

    // FBA stock map
    const stockMap = new Map();
    if (fbaStockData.success && Array.isArray(fbaStockData.data)) {
      fbaStockData.data.forEach(stockItem => {
        if (stockItem.ASIN) {
          stockMap.set(stockItem.ASIN, {
            available: stockItem.available || 0,
            inbound_quantity: stockItem.inbound_quantity || 0,
            reserved_fc_transfer: stockItem.reserved_fc_transfer || 0,
            reserved_fc_processing: stockItem.reserved_fc_processing || 0,
            total_quantity: stockItem.total_quantity || 0
          });
        }
      });
    }

    // AWD stock map
    const awdStockMap = new Map();
    if (awdStockData.success && Array.isArray(awdStockData.data)) {
      awdStockData.data.forEach(awdItem => {
        if (awdItem.ASIN) {
          const totalAwd = (awdItem.inbound_to_awd || 0) + (awdItem.available_in_awd || 0);
          awdStockMap.set(awdItem.ASIN, totalAwd);
        }
      });
    }

    // WH stock map (by SKU + source)
    const whStockMap = new Map();
    if (setsWhStockData.success && Array.isArray(setsWhStockData.data)) {
      setsWhStockData.data.forEach(item => {
        if (item.SKU) whStockMap.set(`sets-${item.SKU}`, item.whStock || 0);
      });
    }
    if (wildmicrWhStockData.success && Array.isArray(wildmicrWhStockData.data)) {
      wildmicrWhStockData.data.forEach(item => {
        if (item.SKU) whStockMap.set(`wildmicr-${item.SKU}`, item.whStock || 0);
      });
    }
    if (individualPacketsWhStockData.success && Array.isArray(individualPacketsWhStockData.data)) {
      individualPacketsWhStockData.data.forEach(item => {
        if (item.SKU) whStockMap.set(`individualpackets-${item.SKU}`, item.whStock || 0);
      });
    }

    const getWhStock = (sku, source) => {
      if (!sku) return 0;
      return whStockMap.get(`${source}-${sku}`) || 0;
    };

    // Sets
    if (setsData.success && Array.isArray(setsData.data)) {
      const setsProducts = setsData.data
        .filter(item => item.ASIN && item.ASIN.trim() !== '')
        .map((item, index) => {
          const asin = item.ASIN.trim();
          const stockData = stockMap.get(asin);
          const whStock = getWhStock(item.SKU, 'sets');
          const awdStock = awdStockMap.get(asin) || 0;
          const wmStock = walmartStockMap.get(item.SKU?.trim()) || 0;
          return {
            id: `sets-${asin}-${index}`,
            productName: `${item.brandID} - ${item.setmane}`,
            asin: asin,
            sku: item.SKU,
            bagsPerBox: parseInt(item.bagsPerBox) || 0,
            whStock: whStock,
            fbaStock: stockData ? stockData.total_quantity : 0,
            awdStock: awdStock,
            wmStock: wmStock, // <-- Walmart stock
            stockDetails: stockData || null,
            source: 'sets',
            brandID: item.brandID,
            niche_category_id: item.niche_category_id
          };
        });
      allProducts = [...allProducts, ...setsProducts];
    }

    // Wildmicr
    if (wildmicrData.success && Array.isArray(wildmicrData.data)) {
      const wildmicrProducts = wildmicrData.data
        .filter(item => item.ASIN && item.ASIN.trim() !== '')
        .map((item, index) => {
          const asin = item.ASIN.trim();
          const stockData = stockMap.get(asin);
          const whStock = getWhStock(item.SKU, 'wildmicr');
          const awdStock = awdStockMap.get(asin) || 0;
          const wmStock = walmartStockMap.get(item.SKU?.trim()) || 0;
          return {
            id: `wildmicr-${asin}-${index}`,
            productName: `${item.brandID} - ${item.packName}`,
            asin: asin,
            sku: item.SKU,
            bagsPerBox: parseInt(item.bagsPerBox) || 0,
            whStock: whStock,
            fbaStock: stockData ? stockData.total_quantity : 0,
            awdStock: awdStock,
            wmStock: wmStock,
            stockDetails: stockData || null,
            source: 'wildmicr',
            brandID: item.brandID,
            niche_category_id: item.niche_category_id
          };
        });
      allProducts = [...allProducts, ...wildmicrProducts];
    }

    // Individual packets
    if (individualPacketsData.success && Array.isArray(individualPacketsData.data)) {
      const individualPacketsProducts = individualPacketsData.data
        .filter(item => item.ASIN && item.ASIN.trim() !== '')
        .map((item, index) => {
          const asin = item.ASIN.trim();
          const stockData = stockMap.get(asin);
          const whStock = getWhStock(item.SKU, 'individualpackets');
          const awdStock = awdStockMap.get(asin) || 0;
          const wmStock = walmartStockMap.get(item.SKU?.trim()) || 0;
          return {
            id: `individualpackets-${asin}-${index}`,
            productName: `${item.brandID} - ${item.packName}`,
            asin: asin,
            sku: item.SKU,
            bagsPerBox: parseInt(item.bagsPerBox) || 0,
            whStock: whStock,
            fbaStock: stockData ? stockData.total_quantity : 0,
            awdStock: awdStock,
            wmStock: wmStock,
            stockDetails: stockData || null,
            source: 'individualpackets',
            brandID: item.brandID,
            niche_category_id: item.niche_category_id || null
          };
        });
      allProducts = [...allProducts, ...individualPacketsProducts];
    }

    // Kits — WH stock embedded in /yoy-kits response
    if (kitsData.success && Array.isArray(kitsData.data)) {
      const kitsProducts = kitsData.data
        .filter(item => item.ASIN && item.ASIN.trim() !== '')
        .map((item, index) => {
          const asin = item.ASIN.trim();
          const stockData = stockMap.get(asin);
          const awdStock = awdStockMap.get(asin) || 0;
          const wmStock = walmartStockMap.get(item.SKU?.trim()) || 0;
          return {
            id: `kit-${asin}-${index}`,
            productName: `${item.brandID} - ${item.kitName}`,
            asin,
            sku: item.SKU,
            bagsPerBox: parseInt(item.unitsPerBox) || 1,
            whStock: item.whStock || 0,
            fbaStock: stockData ? stockData.total_quantity : 0,
            awdStock,
            wmStock,
            stockDetails: stockData || null,
            source: 'kit',
            brandID: item.brandID,
            niche_category_id: null,
          };
        });
      allProducts = [...allProducts, ...kitsProducts];
    }

    // Fertilizers
    if (fertData.success && Array.isArray(fertData.data)) {
      const fertProducts = fertData.data
        .filter(item => item.ASIN && item.ASIN.trim() !== '')
        .map((item, index) => {
          const asin = item.ASIN.trim();
          const stockData = stockMap.get(asin);
          const awdStock = awdStockMap.get(asin) || 0;
          const wmStock = walmartStockMap.get(item.SKU?.trim()) || 0;
          return {
            id: `fert_product-${asin}-${index}`,
            productName: `${item.brandID} - ${item.productName}`,
            asin,
            sku: item.SKU,
            bagsPerBox: parseInt(item.bagsPerBox) || 1,
            whStock: item.whStock || 0,
            fbaStock: stockData ? stockData.total_quantity : 0,
            awdStock,
            wmStock,
            stockDetails: stockData || null,
            source: 'fert_product',
            brandID: item.brandID,
            niche_category_id: null,
          };
        });
      allProducts = [...allProducts, ...fertProducts];
    }

    // Ready Products
    if (readyData.success && Array.isArray(readyData.data)) {
      const readyProducts = readyData.data
        .filter(item => item.ASIN && item.ASIN.trim() !== '')
        .map((item, index) => {
          const asin = item.ASIN.trim();
          const stockData = stockMap.get(asin);
          const awdStock = awdStockMap.get(asin) || 0;
          const wmStock = walmartStockMap.get(item.SKU?.trim()) || 0;
          return {
            id: `readyproduct-${asin}-${index}`,
            productName: `${item.brandID} - ${item.productName}`,
            asin,
            sku: item.SKU,
            bagsPerBox: parseInt(item.unitsPerBox) || 1,
            whStock: item.whStock || 0,
            fbaStock: stockData ? stockData.total_quantity : 0,
            awdStock,
            wmStock,
            stockDetails: stockData || null,
            source: 'readyproduct',
            brandID: item.brandID,
            niche_category_id: null,
          };
        });
      allProducts = [...allProducts, ...readyProducts];
    }

    // Sort products
    const order = { sets: 1, wildmicr: 2, individualpackets: 3, kit: 4, fert_product: 5, readyproduct: 6 };
    const sortedProducts = allProducts.sort((a, b) => {
      const sourceOrder = (order[a.source] || 4) - (order[b.source] || 4);
      if (sourceOrder !== 0) return sourceOrder;
      return a.productName.localeCompare(b.productName);
    });

    setProductsData(sortedProducts);
  } catch (error) {
    console.error('Error in loadProductsFromIndividualEndpoints:', error);
    throw error;
  }
};

  const loadAWDStockMap = async () => {
    try {
      const { data: result } = await api.get(`/api/awd-data?limit=1000`);
      if (result.success && Array.isArray(result.data)) {
        const awdMap = {};
        result.data.forEach(item => {
          if (item.ASIN) {
            const totalAwd = (parseInt(item.inbound_to_awd) || 0) + (parseInt(item.available_in_awd) || 0);
            awdMap[item.ASIN.trim().toUpperCase()] = totalAwd;
          }
        });
        return awdMap;
      }
      return {};
    } catch (error) {
      console.error('Error loading AWD stock data:', error);
      return {};
    }
  };

  const getExampleProducts = () => {
    return [
      {
        id: 'sets-1',
        productName: 'BR001 - Vegetable Seeds Collection',
        asin: 'B0CYTJ3BT7',
        sku: 'VEG-SET-001',
        bagsPerBox: 10,
        whStock: 25,
        fbaStock: 120,
        awdStock: 15,
        stockDetails: { total_quantity: 120 },
        source: 'sets',
        brandID: 'BR001',
        niche_category_id: 1
      },
      {
        id: 'sets-2',
        productName: 'BR002 - Herb Garden Set',
        asin: 'B0CYTJ3BT8',
        sku: 'HERB-SET-002',
        bagsPerBox: 8,
        whStock: 15,
        fbaStock: 85,
        awdStock: 10,
        stockDetails: { total_quantity: 85 },
        source: 'sets',
        brandID: 'BR002',
        niche_category_id: 2
      },
      {
        id: 'wildmicr-1',
        productName: 'WM001 - Wildflower Mix Pack',
        asin: 'B0CB1W87DL',
        sku: 'WILD-MIX-001',
        bagsPerBox: 12,
        whStock: 40,
        fbaStock: 95,
        awdStock: 5,
        stockDetails: { total_quantity: 95 },
        source: 'wildmicr',
        brandID: 'WM001',
        niche_category_id: 1
      },
      {
        id: 'individualpackets-1',
        productName: 'IP001 - Tomato Seeds Packet',
        asin: 'B09XYZ1234',
        sku: 'TOM-SEEDS-001',
        bagsPerBox: 20,
        whStock: 100,
        fbaStock: 200,
        awdStock: 0,
        stockDetails: { total_quantity: 200 },
        source: 'individualpackets',
        brandID: 'IP001',
        niche_category_id: null
      }
    ];
  };

  // --------------------------------------------------------------
  // BRANDS & NICHES
  // --------------------------------------------------------------
  const loadBrandsAndNiches = async () => {
    try {
      const brandsResult = (await api.get(`/api/yoy-brands`)).data;
      const brands = brandsResult.data || [];
      setBrandsData(brands);
      // По умолчанию показываем только Organo Republic — Seedphony скрыт,
      // пока пользователь сам не поменяет галочки (запрос 02.09.2026)
      const or = brands.find(b => /organo/i.test(b.brandName || ''));
      if (or) setSelectedBrands(prev => (prev.length === 0 ? [or.brandID] : prev));
    } catch (error) {
      console.error('Error loading brands or niches:', error);
    }
    try {
      const nichesResult = (await api.get(`/api/yoy-niches`)).data;
      setNichesData(nichesResult.data || []);
    } catch (error) {
      console.error('Error loading brands or niches:', error);
    }
  };

  // --------------------------------------------------------------
  // PLAN TO PRODUCE DATA (masterbags_dailyplan)
  // --------------------------------------------------------------
  const loadPlanToProduceData = async (products) => {
    try {
      const skus = (products || productsData)
        .map(p => p.sku)
        .filter(Boolean);
      if (skus.length === 0) return;
      const url = `/api/daily-plan-totals?skus=${skus.join(',')}`;
      const { data: result } = await api.get(url);
      if (result.success) {
        setPlanToProduceData(result.data || {});
      }
    } catch (error) {
      console.error('Error loading plan to produce data:', error);
    }
  };

  // --------------------------------------------------------------
  // MONTHLY PLAN TO PRODUCE DATA LOADING
  // --------------------------------------------------------------
  const loadMonthlyPlanData = async (products) => {
    try {
      const skus = (products || productsData)
        .map(p => p.sku)
        .filter(Boolean);
      if (skus.length === 0) return;
      // POST чтобы избежать ERR_CONNECTION_RESET от слишком длинного URL
      const { data: result } = await api.post(`/api/yoy-monthly-plan-to-produce`, { skus });
      if (result.success) {
        setMonthlyPlanData(result.data || {});
        setMonthlyPlanMeta({ months: result.months || [] });
      }
    } catch (error) {
      console.error('Error loading monthly plan to produce data:', error);
    }
  };

  // --------------------------------------------------------------
  // WEEKLY PLAN TO PRODUCE DATA LOADING
  // --------------------------------------------------------------
  const loadWeeklyPlanData = async (products) => {
    try {
      const skus = (products || productsData)
        .map(p => p.sku)
        .filter(Boolean);
      if (skus.length === 0) return;
      const { data: result } = await api.post(`/api/yoy-weekly-plan-to-produce`, { skus });
      if (result.success) {
        setWeeklyPlanData(result.data || {});
      }
    } catch (error) {
      console.error('Error loading weekly plan to produce data:', error);
    }
  };

  const loadSeasonalityFlags = async (asins) => {
    if (!asins || asins.length === 0) return {};
    try {
      const { data: result } = await api.get(`/api/yoy-seasonality-flags?asins=${asins.join(',')}`);
      const flags = {};
      result.data.forEach(item => {
        flags[item.asin] = item.seasonality_enabled === 1;
      });
      setSeasonalityFlags(flags);
      return flags;
    } catch (error) {
      console.error('Error loading seasonality flags:', error);
    }
    return {};
  };

  const toggleSeasonalityFlag = async (asin, checked) => {
    setSeasonalityFlags(prev => ({ ...prev, [asin]: checked }));
    try {
      await api.post(`/api/yoy-seasonality-flag`, { asin, enabled: checked });
    } catch (error) {
      setSeasonalityFlags(prev => ({ ...prev, [asin]: !checked }));
      if (error.response) {
        console.error('Failed to update flag');
      } else {
        console.error('Error updating flag:', error);
      }
    }
  };

  // --------------------------------------------------------------
  // SEASONALITY COEFFICIENTS 
  // --------------------------------------------------------------
  const seasonalityCoeffMap = useMemo(() => {
    const map = {};
    seasonalityCoefficients.forEach(item => {
      const key = `${item.marketplace || 'amazon'}-${item.niche_category_id}-${item.month_num}`;
      map[key] = parseFloat(item.seasonality_coefficient);
    });
    return map;
  }, [seasonalityCoefficients]);

  const getSeasonalityCoefficient = useCallback((nicheId, month) => {
    return seasonalityCoeffMap[`${marketplaceConfig.id}-${nicheId}-${month}`] || 1.0;
  }, [seasonalityCoeffMap, marketplaceConfig.id]);

  const getMonthFromWeek = useCallback((year, week) => {
    const date = getDateOfISOWeek(year, week);
    return date.getMonth() + 1;
  }, []);

  const calculateSeasonalityForecast = useCallback((
    product,
    targetWeek,
    targetYear,
    marketplace
  ) => {
    const asin = product.asin;
    const nicheId = product.niche_category_id;
    if (!nicheId) return 'N/A';

    const cacheKey = `${marketplace}-${asin}-${targetYear}-W${targetWeek}`;
    if (seasonalityForecastCache.current[cacheKey] !== undefined) {
      return seasonalityForecastCache.current[cacheKey];
    }

    const config = MARKETPLACE_CONFIGS[marketplace] ?? MARKETPLACE_CONFIGS['amazon'];
    const identifier = product[config.identifierField];
    const salesData = salesDataByMarketplace[marketplace] || {};

    // Поиск самой свежей фактической недели, предшествующей целевой.
    // Вместо O(N) скана всего salesData — детерминированный поиск назад (до 104 недель = 2 года).
    let latestSales = 0;
    let latestWeek = null;
    let latestYear = null;

    for (let offset = 1; offset <= 104; offset++) {
      let w = targetWeek - offset;
      let y = targetYear;
      while (w < 1) { w += 52; y--; }
      const key = `${identifier}-${y}-W${w}`;
      const record = salesData[key];
      if (record && typeof record.units === 'number' && record.units > 0) {
        latestWeek = w;
        latestYear = y;
        latestSales = record.units;
        break;
      }
    }

    // Если нет ни одной недели с фактическими продажами – прогноз невозможен
    if (latestWeek === null) return 'N/A';

    // Инициализация цикла прогнозирования
    let currentWeek = latestWeek + 1;
    let currentYear = latestYear;
    let prevSales = latestSales;
    let prevWeek = latestWeek;
    let prevYear = latestYear;
    let prevMonth = getMonthFromWeek(prevYear, prevWeek);
    let coeffPrev = getSeasonalityCoefficient(nicheId, prevMonth);

    // Прогнозируем недели последовательно, пока не достигнем целевой
    while (
      currentYear < targetYear ||
      (currentYear === targetYear && currentWeek <= targetWeek)
    ) {
      // Коррекция перехода через конец года
      if (currentWeek > 52) {
        currentWeek = 1;
        currentYear++;
      }

      const currentMonth = getMonthFromWeek(currentYear, currentWeek);
      const coeffCurr = getSeasonalityCoefficient(nicheId, currentMonth);
      const forecast = Math.round((prevSales / coeffPrev) * coeffCurr);

      // Сохраняем прогноз в кэш
      const key = `${marketplace}-${asin}-${currentYear}-W${currentWeek}`;
      seasonalityForecastCache.current[key] = forecast;

      // Переход к следующей неделе
      prevSales = forecast;
      prevWeek = currentWeek;
      prevYear = currentYear;
      coeffPrev = coeffCurr;
      prevMonth = currentMonth;

      currentWeek++;
      if (currentWeek > 52) {
        currentWeek = 1;
        currentYear++;
      }
    }

    return seasonalityForecastCache.current[cacheKey];
  }, [
    salesDataByMarketplace,
    getMonthFromWeek,
    getSeasonalityCoefficient,
    seasonalityForecastCache,
    getDateOfISOWeek,   // <-- добавлена зависимость
  ]);

  // --------------------------------------------------------------
  // SALES DATA LOADING (per marketplace)
  // --------------------------------------------------------------
  // Compute median from pre-indexed data for a given ASIN.
  // Accepts indexedByAsin: { [asin]: [{ y, w, units }] } for O(1) asin lookup.
  // using N clean (no-flag) weeks that come before flagWeekNum/flagYearNum.
  const computeMedianFromData = (indexedByAsin, asin, flagWeekNum, flagYearNum, count) => {
    const entries = indexedByAsin[asin] || [];
    const cleanUnits = entries
      .filter(({ y, w, units }) => {
        if (y > flagYearNum) return false;
        if (y === flagYearNum && w >= flagWeekNum) return false;
        return true;
      });
    if (cleanUnits.length === 0) return null;
    // Sort descending (most recent first), take N weeks
    const sorted_entries = [...cleanUnits].sort((a, b) => b.y - a.y || b.w - a.w);
    const slice = sorted_entries.slice(0, count);
    const sorted = slice.map(x => x.units).sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
      ? Math.round((sorted[mid - 1] + sorted[mid]) / 2)
      : sorted[mid];
  };

    const loadSalesDataForMarketplace = async (marketplace, histCount = null, foreCount = null, medianCount = null) => {
    if (!productsData.length) return;

    const config = MARKETPLACE_CONFIGS[marketplace] ?? MARKETPLACE_CONFIGS['amazon'];
    const endpoint = config.endpoints.salesRange;
    if (!endpoint) {
      console.warn(`No salesRange endpoint for marketplace ${marketplace}`);
      setSalesDataByMarketplace(prev => ({ ...prev, [marketplace]: {} }));
      clearSeasonalityForecastCache();
      return;
    }

    setLoadingSales(true);

    try {
      const weekInfo = getCurrentWeekAndYear();
      if (marketplace === activeMarketplace) {
        setCurrentWeekInfo(weekInfo);
      }

      const histCountToUse = histCount !== null ? histCount : historicalWeeksCount;
      const foreCountToUse = foreCount !== null ? foreCount : forecastWeeksCount;

      const historicalWeeksData = getHistoricalWeeks(weekInfo.weekNumber, weekInfo.year, histCountToUse);
      const forecastWeeksData = getForecastWeeks(weekInfo.weekNumber, weekInfo.year, foreCountToUse);

      if (marketplace === activeMarketplace) {
        setHistoricalWeeks(historicalWeeksData);
        setForecastWeeks(forecastWeeksData);
      }

      // Get identifiers for this marketplace
      const validProducts = productsData.filter(config.productFilter);
      const identifiers = validProducts.map(p => p[config.identifierField]).filter(Boolean);

      if (identifiers.length === 0) {
        setSalesDataByMarketplace(prev => ({ ...prev, [marketplace]: {} }));
        setLoadingSales(false);
        return;
      }

      const allWeeks = [
        ...historicalWeeksData.map(w => ({ week: w.weekNumber, year: w.year, previousYear: w.year - 1 })),
        { week: weekInfo.weekNumber, year: weekInfo.year, previousYear: weekInfo.year - 1 },
        ...forecastWeeksData.map(w => ({ week: w.weekNumber, year: w.year, previousYear: w.year - 1 }))
      ];

      // СТАЛО:
      const yearRanges = {};
      allWeeks.forEach(w => {
        [w.year, w.previousYear].forEach(y => {
          if (!yearRanges[y]) yearRanges[y] = { min: w.week, max: w.week };
          else {
            yearRanges[y].min = Math.min(yearRanges[y].min, w.week);
            yearRanges[y].max = Math.max(yearRanges[y].max, w.week);
          }
        });
      });

      // Расширяем минимальную неделю каждого года на medianWeeksCount назад,
      // чтобы при расчёте медианы для флагнутых записей хватало чистых недель.
          if (config.supportsFlags) {
            const medianCountToUse = medianCount !== null ? medianCount : medianWeeksCount;
            Object.keys(yearRanges).forEach(y => {
              const newMin = yearRanges[y].min - medianCountToUse;
              if (newMin < 1) {
                // Переходим в предыдущий год
                const prevYear = parseInt(y, 10) - 1;
                const weeksInPrevYear = 52; // упрощение (достаточно для ISO)
                const prevYearMin = weeksInPrevYear + newMin; // newMin отрицательный
                if (!yearRanges[prevYear]) yearRanges[prevYear] = { min: prevYearMin, max: weeksInPrevYear };
                else {
                  yearRanges[prevYear].min = Math.min(yearRanges[prevYear].min, prevYearMin);
                  yearRanges[prevYear].max = Math.max(yearRanges[prevYear].max, weeksInPrevYear);
                }
                yearRanges[y].min = 1;
              } else {
                yearRanges[y].min = newMin;
              }
            });
          }

      const allSalesData = {};
      const promises = Object.entries(yearRanges).map(([year, range]) => {
        const url = `${endpoint}?${config.identifierField}s=${identifiers.join(',')}&startWeek=${range.min}&startYear=${year}&endWeek=${range.max}&endYear=${year}`;
        return api.get(url)
          .then(res => res.data)
          .then(result => {
            if (result.success) {
              Object.assign(allSalesData, result.data);
            }
          })
          .catch(err => console.error(`Error loading ${marketplace} sales for year ${year}:`, err));
      });

      await Promise.all(promises);

      const processed = {};
      Object.entries(allSalesData).forEach(([key, value]) => {
        const hasFlag = value.has_flag === 1 || value.has_flag === true ||
          value.best_seller === 1 || value.lightning_deal === 1 ||
          value.new_product === 1 || value.out_of_stock === 1;
        processed[key] = {
          value: value.value,
          units: value.units,
          hasFlag: hasFlag ? 1 : 0,
          best_seller: value.best_seller,
          lightning_deal: value.lightning_deal,
          new_product: value.new_product,
          out_of_stock: value.out_of_stock
        };
      });

      // For flagged records — compute median from already-loaded data and use as display value.
      // DB value (units) is never changed. All forecasts use record.value which will be the median.
      if (config.supportsFlags) {
        const medianCountToUse = medianCount !== null ? medianCount : medianWeeksCount;

        // Pre-index clean (non-flagged) entries by asin for O(1) lookup in computeMedianFromData.
        // Without this, computeMedianFromData does O(N) scan of the entire salesMap per flagged record.
        const indexedByAsin = {};
        Object.entries(processed).forEach(([k, r]) => {
          if (r.hasFlag) return;
          if (typeof r.units !== 'number') return;
          const m = k.match(/^(.+)-(\d+)-W(\d+)$/);
          if (!m) return;
          const asin = m[1];
          const y = parseInt(m[2], 10);
          const w = parseInt(m[3], 10);
          if (!indexedByAsin[asin]) indexedByAsin[asin] = [];
          indexedByAsin[asin].push({ y, w, units: r.units });
        });

        Object.entries(processed).forEach(([key, rec]) => {
          if (!rec.hasFlag) return;
          const match = key.match(/^(.+)-(\d+)-W(\d+)$/);
          if (!match) return;
          const [, asin, flagYear, flagWeek] = match;
          const median = computeMedianFromData(
            indexedByAsin, asin,
            parseInt(flagWeek, 10), parseInt(flagYear, 10),
            medianCountToUse
          );
          if (median !== null) {
            processed[key] = { ...rec, value: median, medianValue: median };
          }
        });
      }

      setSalesDataByMarketplace(prev => ({ ...prev, [marketplace]: processed }));

      // Передаём processed и weekInfo напрямую — React ещё не применил setState,
      // поэтому и salesDataByMarketplace и currentWeekInfo из замыкания могут быть устаревшими.
      updateDaysSuppliedDataForMarketplace(marketplace, processed, weekInfo);

    } catch (error) {
      console.error(`Error loading sales data for ${marketplace}:`, error);
    } finally {
      setLoadingSales(false);
    }
  };

  // Convenience wrapper for AdminPanel etc.
  const loadSalesData = (histCount, foreCount) => {
    return loadSalesDataForMarketplace(activeMarketplace, histCount, foreCount);
  };

  // --------------------------------------------------------------
  // MONTHLY SALES DATA LOADER
  // Загружает данные из *_month_sales таблиц (ключи: IDENTIFIER-YYYY-MM)
  // --------------------------------------------------------------
  const loadMonthlySalesDataForMarketplace = async (marketplace, histCount = null, foreCount = null) => {
    if (!productsData.length) return;

    const config   = MARKETPLACE_CONFIGS[marketplace] ?? MARKETPLACE_CONFIGS['amazon'];
    const endpoint = config.endpoints.monthSalesRange;
    if (!endpoint) {
      console.warn(`No monthSalesRange endpoint for marketplace ${marketplace}`);
      setMonthlySalesDataByMarketplace(prev => ({ ...prev, [marketplace]: {} }));
      return;
    }

    setLoadingSales(true);

    try {
      const monthInfo    = getCurrentMonthInfo();
      const histCountUse = histCount !== null ? histCount : historicalMonthsCount;
      const foreCountUse = foreCount !== null ? foreCount : forecastMonthsCount;

      const historicalMonthsData = getHistoricalMonths(monthInfo.month, monthInfo.year, histCountUse);
      const forecastMonthsData   = getForecastMonths(monthInfo.month, monthInfo.year, foreCountUse);

      // Диапазон месяцев включая предыдущий год для YoY
      const { startYear, startMonth, endYear, endMonth } = buildMonthlyQueryRange(
        historicalMonthsData, monthInfo, forecastMonthsData
      );

      const validProducts = productsData.filter(config.productFilter);
      const identifiers   = validProducts.map(p => p[config.identifierField]).filter(Boolean);

      if (identifiers.length === 0) {
        setMonthlySalesDataByMarketplace(prev => ({ ...prev, [marketplace]: {} }));
        return;
      }

      const url = `${endpoint}?${config.identifierField}s=${identifiers.join(',')}&startMonth=${startMonth}&startYear=${startYear}&endMonth=${endMonth}&endYear=${endYear}`;

      const result = (await api.get(url)).data;

      if (result.success) {
  const rawData = result.data;

  // Нормализуем структуру записей (как в weekly loader)
  const processed = {};
  Object.entries(rawData).forEach(([key, value]) => {
    const hasFlag =
      value.has_flag === 1 || value.has_flag === true ||
      value.best_seller === 1 || value.lightning_deal === 1 ||
      value.new_product === 1 || value.out_of_stock === 1;
    processed[key] = {
      value:          value.value ?? value.units,
      units:          value.units,
      has_flag:       hasFlag ? 1 : 0,
      hasFlag:        hasFlag ? 1 : 0,
      best_seller:    value.best_seller,
      lightning_deal: value.lightning_deal,
      new_product:    value.new_product,
      out_of_stock:   value.out_of_stock,
    };
  });

  // Для флагованных записей — считаем медиану из чистых месяцев и используем
  // её как value (как это делается в weekly loader для прогнозов).
  // Ключи имеют формат: IDENTIFIER-YYYY-MM
  if (config.supportsFlags) {
    // Индексируем чистые (без флагов) записи по identifier для быстрого поиска
    const indexedByIdentifier = {};
    Object.entries(processed).forEach(([k, r]) => {
      if (r.hasFlag) return;
      if (typeof r.units !== 'number') return;
      const m = k.match(/^(.+)-(\d+)-(\d+)$/);
      if (!m) return;
      const identifier = m[1];
      const y  = parseInt(m[2], 10);
      const mo = parseInt(m[3], 10);
      if (!indexedByIdentifier[identifier]) indexedByIdentifier[identifier] = [];
      indexedByIdentifier[identifier].push({ y, mo, units: r.units });
    });

    Object.entries(processed).forEach(([key, rec]) => {
      if (!rec.hasFlag) return;
      const m = key.match(/^(.+)-(\d+)-(\d+)$/);
      if (!m) return;
      const [, identifier, flagYearStr, flagMonthStr] = m;
      const flagYear  = parseInt(flagYearStr, 10);
      const flagMonth = parseInt(flagMonthStr, 10);

      const entries = (indexedByIdentifier[identifier] || [])
        .filter(({ y, mo }) => {
          if (y > flagYear) return false;
          if (y === flagYear && mo >= flagMonth) return false;
          return true;
        })
        // Сортируем по убыванию (самые свежие первые), берём N месяцев
        .sort((a, b) => b.y - a.y || b.mo - a.mo)
        .slice(0, medianWeeksCount);

      if (entries.length === 0) return;

      const sorted = entries.map(x => x.units).sort((a, b) => a - b);
      const mid    = Math.floor(sorted.length / 2);
      const median = sorted.length % 2 === 0
        ? Math.round((sorted[mid - 1] + sorted[mid]) / 2)
        : sorted[mid];

      processed[key] = { ...rec, value: median, medianValue: median };
    });
  }

  setMonthlySalesDataByMarketplace(prev => ({ ...prev, [marketplace]: processed }));
      } else {
        console.error(`Monthly sales load failed for ${marketplace}:`, result);
      }
    } catch (error) {
      console.error(`Error loading monthly sales for ${marketplace}:`, error);
    } finally {
      setLoadingSales(false);
    }
  };

  // --------------------------------------------------------------
  // DAYS SUPPLIED (per marketplace)
  // --------------------------------------------------------------
 
  const updateDaysSuppliedDataForMarketplace = (marketplace, salesDataOverride = null, weekInfoOverride = null) => {
    // weekInfoOverride нужен при вызове сразу после setCurrentWeekInfo —
    // React ещё не применил setState, поэтому currentWeekInfo из замыкания может быть null.
    const effectiveWeekInfo = weekInfoOverride ?? currentWeekInfo;
    if (!productsData.length || !effectiveWeekInfo) return;

    const config = MARKETPLACE_CONFIGS[marketplace] ?? MARKETPLACE_CONFIGS['amazon'];
    // salesDataOverride позволяет передать свежие данные напрямую, минуя React state,
    // который ещё не обновился в момент вызова после setSalesDataByMarketplace.
    const salesForMarket = salesDataOverride ?? salesDataByMarketplace[marketplace] ?? {};

    const getSalesForMarket = (identifier, year, week) => {
      const key = `${identifier}-${year}-W${week}`;
      return salesForMarket[key]?.value ?? 'N/A';
    };

    // calculateAverageGrowth тоже должна использовать salesForMarket (не замыкание state),
    // иначе при первой загрузке она читает пустой salesDataByMarketplace.
    const localCalculateAverageGrowth = (identifier) => {
      const getSales = (id, year, week) => {
        const key = `${id}-${year}-W${week}`;
        return salesForMarket[key]?.value ?? 'N/A';
      };
      return calculateAverageGrowth(identifier, historicalWeeks, getSales);
    };

    // Локальная forecast-функция, которая использует salesForMarket напрямую
    // (не через замыкание salesDataByMarketplace, который ещё не обновлён в state).
    const localCalculateForecast = (identifier, weekNumber, year, averageGrowth) => {
      const getSales = (id, y, w) => {
        const key = `${id}-${y}-W${w}`;
        return salesForMarket[key]?.value ?? 'N/A';
      };
      return calculateForecast(identifier, weekNumber, year, averageGrowth, getSales);
    };

    const newData = {};

    productsData.forEach(product => {
      if (!config.productFilter(product)) return;
      const identifier = product[config.identifierField];
      if (!identifier) return;

      const wh = Number(product.whStock) || 0;

      // 🔁 Функция прогноза: используем localCalculateForecast (свежие данные),
      // сезонность при первой загрузке пропускаем если кеш ещё пустой.
      const forecastWithSeasonality = (id, week, year, growth) => {
        if (config.hasSeasonality && product.niche_category_id && seasonalityFlags[product.asin]) {
          const seasonal = calculateSeasonalityForecast(product, week, year, marketplace);
          if (seasonal !== 'N/A') return seasonal;
        }
        return localCalculateForecast(identifier, week, year, growth);
      };

      if (marketplace === 'amazon') {
        const fba = Number(product.fbaStock) || 0;
        const awd = Number(product.awdStock) || 0;
        const amazonStock = fba + awd;
        const totalStock = fba + awd + wh;

        const daysAmazon = calculateDaysSupplied(
          amazonStock,
          identifier,
          null,
          null,
          effectiveWeekInfo,
          localCalculateAverageGrowth,
          forecastWithSeasonality,
          getSalesForMarket
        );

        const daysTotal = calculateDaysSupplied(
          totalStock,
          identifier,
          null,
          null,
          effectiveWeekInfo,
          localCalculateAverageGrowth,
          forecastWithSeasonality,
          getSalesForMarket
        );

        newData[identifier] = { amazon: daysAmazon, total: daysTotal };
      } else if (marketplace === 'walmart') {
        const wm = Number(product.wmStock) || 0;
        const totalStock = wh + wm;

        const daysWalmart = calculateDaysSupplied(
          wm,
          identifier,
          null,
          null,
          effectiveWeekInfo,
          localCalculateAverageGrowth,
          forecastWithSeasonality,
          getSalesForMarket
        );

        const daysTotal = calculateDaysSupplied(
          totalStock,
          identifier,
          null,
          null,
          effectiveWeekInfo,
          localCalculateAverageGrowth,
          forecastWithSeasonality,
          getSalesForMarket
        );

        newData[identifier] = { walmart: daysWalmart, total: daysTotal };
      } else {
        // Shopify, Etsy и т.д. – только WH
        const daysTotal = calculateDaysSupplied(
          wh,
          identifier,
          null,
          null,
          effectiveWeekInfo,
          localCalculateAverageGrowth,
          forecastWithSeasonality,
          getSalesForMarket
        );
        newData[identifier] = { total: daysTotal };
      }
    });

    setDaysSuppliedDataByMarketplace(prev => ({ ...prev, [marketplace]: newData }));
  };

  // --------------------------------------------------------------
  // FORECAST & GROWTH HELPERS (marketplace‑aware)
  // --------------------------------------------------------------
  const clearSeasonalityForecastCache = useCallback(() => {
    seasonalityForecastCache.current = {};
    yoyForecastCache.current = {};
  }, []);

  const calculateAverageGrowthForProduct = useCallback((identifier, marketplace = activeMarketplace) => {
    const sales = salesDataByMarketplace[marketplace] || {};
    const getSales = (id, year, week) => {
      const key = `${id}-${year}-W${week}`;
      return sales[key]?.value ?? 'N/A';
    };
    return calculateAverageGrowth(identifier, historicalWeeks, getSales);
  }, [salesDataByMarketplace, historicalWeeks, activeMarketplace]);

  const calculateForecastForProduct = useCallback((identifier, weekNumber, year, averageGrowth, marketplace = activeMarketplace) => {
    const cacheKey = `${marketplace}-${identifier}-${year}-W${weekNumber}-${Math.round(averageGrowth * 100)}`;
    if (yoyForecastCache.current[cacheKey] !== undefined) {
      return yoyForecastCache.current[cacheKey];
    }
    const sales = salesDataByMarketplace[marketplace] || {};
    const getSales = (id, y, w) => {
      const key = `${id}-${y}-W${w}`;
      return sales[key]?.value ?? 'N/A';
    };
    const result = calculateForecast(identifier, weekNumber, year, averageGrowth, getSales);
    yoyForecastCache.current[cacheKey] = result;
    return result;
  }, [salesDataByMarketplace, activeMarketplace]);

  const calculateForecastForProductConditional = useCallback((
    product,
    weekNumber,
    year,
    averageGrowth,
    marketplace = activeMarketplace
  ) => {
    const config = MARKETPLACE_CONFIGS[marketplace] ?? MARKETPLACE_CONFIGS['amazon'];
    // Сезонность доступна только если в конфиге включена и у продукта есть ниша
    if (config.hasSeasonality && product.niche_category_id) {
      const asin = product.asin;
      if (seasonalityFlags[asin]) {
        const forecast = calculateSeasonalityForecast(product, weekNumber, year, marketplace);
        if (forecast !== 'N/A') return forecast;
      }
    }
    return calculateForecastForProduct(
      product[config.identifierField],
      weekNumber,
      year,
      averageGrowth,
      marketplace
    );
  }, [activeMarketplace, seasonalityFlags, calculateSeasonalityForecast, calculateForecastForProduct]);

  // --------------------------------------------------------------
  // STOCK BALANCE CALCULATION (for current week + forecast weeks)
  // Formula per week:
  //   startBalance = max(0, prevEndBalance)   ← no negative carryover
  //   endBalance   = startBalance + weeklyProduction - weeklyForecast
  //
  // Initial stock (Week 1 startBalance) = WH + all marketplace stocks + AWD
  // --------------------------------------------------------------
  const calculateWeeklyStockBalances = useCallback((product, avgGrowth) => {
    const sku = product.sku;

    // Total stock: warehouse + all marketplace stock columns + awdStock
    const wh = Number(product.whStock) || 0;
    let marketStock = 0;
    marketplaceConfig.stockColumns.forEach(col => {
      marketStock += Number(product[col.key]) || 0;
    });
    const awd = Number(product.awdStock) || 0;
    const initialStock = wh + marketStock + awd;

    const weeksToCalc = currentWeekInfo
      ? [
          { weekNumber: currentWeekInfo.weekNumber, year: currentWeekInfo.year },
          ...forecastWeeks
        ]
      : [];

    // balances[weekKey] = { endBalance, startBalance, production, forecast }
    const balances = {};
    let prevBalance = initialStock;

    weeksToCalc.forEach(week => {
      const weekKey = `${week.year}-W${week.weekNumber}`;
      const production = (weeklyPlanData[sku] || {})[weekKey] || 0;
      const forecast = calculateForecastForProductConditional(
        product,
        week.weekNumber,
        week.year,
        avgGrowth,
        activeMarketplace
      );
      const forecastNum = typeof forecast === 'number' ? forecast : 0;

      // No negative carryover
      const startBalance = Math.max(0, prevBalance);
      const endBalance = startBalance + production - forecastNum;

      balances[weekKey] = {
        endBalance,
        startBalance,
        production,
        forecast: forecastNum,
        // breakdown string for tooltip
        tooltip: `Stock at start: ${startBalance.toLocaleString()}\n+ Plan to produce: ${production.toLocaleString()}\n− Forecast sales: ${forecastNum.toLocaleString()}\n= End balance: ${endBalance.toLocaleString()}${prevBalance < 0 ? '\n(Negative carry ignored)' : ''}`,
      };

      prevBalance = endBalance;
    });

    return balances;
  }, [
    weeklyPlanData,
    marketplaceConfig,
    currentWeekInfo,
    forecastWeeks,
    calculateForecastForProductConditional,
    activeMarketplace,
  ]);

  // --------------------------------------------------------------
  // BOX SHIPPING CALCULATION
  // --------------------------------------------------------------
  const calculateBoxCanBeShipped = (whStock, bagsPerBox) => {
    const wh = parseInt(whStock, 10);
    const bags = parseInt(bagsPerBox, 10);
    if (isNaN(bags) || bags === 0) return 0;
    return Math.floor(wh / bags);
  };

  // --------------------------------------------------------------
  // MONTHLY PLAN: Days Supply helpers — используют МЕСЯЧНЫЙ прогноз
  // --------------------------------------------------------------

  // Шаг 1: строим индексы продаж и avgGrowth один раз для всех продуктов
  const monthlyForecastDataByProduct = useMemo(() => {
    const monthlySalesData = monthlySalesDataByMarketplace[activeMarketplace] || {};
    const curMonthInfo = getCurrentMonthInfo();
    const histMonths = getHistoricalMonths(curMonthInfo.month, curMonthInfo.year, historicalMonthsCount);
    const result = {};
    productsData.forEach(product => {
      const identifier = product[identifierField];
      if (!identifier) return;
      const index = buildMonthlySalesIndex(identifier, monthlySalesData);
      const avgGrowth = calculateMonthlyAverageGrowthFromIndex(index, histMonths);
      result[identifier] = { index, avgGrowth };
    });
    return result;
  }, [productsData, identifierField, monthlySalesDataByMarketplace, activeMarketplace, historicalMonthsCount]);

  // Шаг 2: getter прогноза — O(1), не строит индекс.
  // Учитывает сезонность (как getForecastValue в YoyMonthlyView), чтобы
  // Days Supply в Monthly Plan колонках использовал те же цифры, что видит пользователь.
  const makeMonthlyForecastGetter = useCallback((product) => {
    const identifier = product[identifierField];
    const data = monthlyForecastDataByProduct[identifier];
    if (!data) return () => 'N/A';
    const { index, avgGrowth } = data;
    const curMonthInfo = getCurrentMonthInfo();
    return (id, year, month) => {
      // Если для этого продукта включена сезонность — возвращаем сезонный прогноз
      if (
        marketplaceConfig.hasSeasonality &&
        product.niche_category_id &&
        seasonalityFlags?.[product.asin]
      ) {
        const seasonal = calculateMonthlySeasonalityForecast(
          identifier, year, month,
          product.niche_category_id,
          index,
          getSeasonalityCoefficient,
          curMonthInfo.month,
          curMonthInfo.year
        );
        if (seasonal !== 'N/A') return seasonal;
      }
      return calculateMonthlyForecastFromIndex(index, year, month, avgGrowth);
    };
  }, [identifierField, monthlyForecastDataByProduct, marketplaceConfig,
      seasonalityFlags, getSeasonalityCoefficient]);

  // Вспомогательная: rolling balance для конкретного месяца плана.
  // Для предыдущих месяцев (до целевого) вычитает прогноз продаж:
  //   — текущий месяц: только оставшиеся дни (с сегодня до конца месяца)
  //   — будущие месяцы: полный месяц
  // Для целевого месяца прибавляет только его план производства.
  // upToKey — ключ месяца 'YYYY-MM' из monthlyDisplayMonths.
  const _stockWithCumulativePlan = useCallback((product, upToKey, forecastGetter) => {
    const skuPlan    = monthlyPlanData[product.sku] || {};
    const identifier = product[identifierField];
    const wh = Number(product.whStock) || 0;
    let marketStock = 0;
    marketplaceConfig.stockColumns.forEach(col => { marketStock += Number(product[col.key]) || 0; });

    const curInfo = getCurrentMonthInfo();
    const today   = new Date();

    // monthlyDisplayMonths уже в хронологическом порядке (с текущего месяца, через границу года)
    const upToIdx = monthlyDisplayMonths.findIndex(m => m.key === upToKey);
    const orderedMonths = upToIdx >= 0 ? monthlyDisplayMonths.slice(0, upToIdx + 1) : [];

    let balance = wh + marketStock;
    let planQty = 0;

    for (const { key, year, month_num } of orderedMonths) {
      const plan = Number(skuPlan[key]) || 0;
      planQty += plan;
      balance  = Math.max(0, balance) + plan;

      if (key !== upToKey && forecastGetter) {
        const forecast    = forecastGetter(identifier, year, month_num);
        const forecastVal = typeof forecast === 'number' ? forecast : 0;

        if (month_num === curInfo.month && year === curInfo.year) {
          // Текущий месяц: вычитаем только оставшиеся дни (включая сегодня)
          const daysInMonth  = new Date(year, month_num, 0).getDate();
          const daysLeft     = daysInMonth - today.getDate() + 1;
          balance -= forecastVal * (daysLeft / daysInMonth);
        } else {
          // Будущие месяцы: полное потребление
          balance -= forecastVal;
        }
      }
    }

    // prevBalance = сток до добавления плана целевого месяца (= остаток за предыдущий месяц)
    const planThisMonth = Number(skuPlan[upToKey]) || 0;
    const prevBalance   = balance - planThisMonth;

    return { totalStock: balance, wh, marketStock, planQty, prevBalance, planThisMonth };
  }, [monthlyPlanData, monthlyDisplayMonths, marketplaceConfig, identifierField]);

  // Days Supply (rolling balance до конкретного месяца) — для breakdown колонок
  // planMonth — объект { key, year, month_num } из monthlyDisplayMonths
  const calculateDaysSupplyWithCumulativePlan = useCallback((product, planMonth) => {
    const getForecast = makeMonthlyForecastGetter(product);
    const curInfo     = getCurrentMonthInfo();
    const { totalStock } = _stockWithCumulativePlan(product, planMonth.key, getForecast);
    const identifier  = product[identifierField];

    // Для будущих месяцев: начинаем расчёт с 1-го числа этого месяца (полный месяц).
    // Для текущего: с сегодня.
    const isFuture  = isFuturePlanMonth(planMonth, curInfo);
    const startInfo = isFuture ? { month: planMonth.month_num, year: planMonth.year } : curInfo;
    const startDay  = isFuture ? 1 : null;

    return calculateDaysSuppliedMonthly(totalStock, identifier, startInfo, getForecast, 24, startDay);
  }, [_stockWithCumulativePlan, identifierField, makeMonthlyForecastGetter]);

  // Days Supply (сумма ВСЕХ месяцев плана) — для колонки Total + Plan
  const calculateDaysSupplyWithTotalPlan = useCallback((product) => {
    const allMonthsQty = Object.values(monthlyPlanData[product.sku] || {})
      .reduce((sum, qty) => sum + (Number(qty) || 0), 0);
    const wh = Number(product.whStock) || 0;
    let marketStock = 0;
    marketplaceConfig.stockColumns.forEach(col => { marketStock += Number(product[col.key]) || 0; });
    const totalStock  = wh + marketStock + allMonthsQty;
    const identifier  = product[identifierField];
    const getForecast = makeMonthlyForecastGetter(product);
    return calculateDaysSuppliedMonthly(totalStock, identifier, getCurrentMonthInfo(), getForecast);
  }, [monthlyPlanData, marketplaceConfig, identifierField, makeMonthlyForecastGetter]);

  // Helper: shared stock breakdown builder for the three popup variants
  const _buildDaysData = useCallback((product, stock, stockBreakdown, title) => {
    const identifier  = product[identifierField];
    const getForecast = makeMonthlyForecastGetter(product);
    const curInfo     = getCurrentMonthInfo();
    const days        = calculateDaysSuppliedMonthly(stock, identifier, curInfo, getForecast);
    const monthLog    = buildMonthlyConsumptionLog(stock, identifier, curInfo, getForecast);
    return { title, productName: product.productName, stockBreakdown, totalStock: stock, days, monthLog };
  }, [identifierField, makeMonthlyForecastGetter]);

  // Days Supply (marketplace only)
  const getMarketplaceDaysData = useCallback((product) => {
    const breakdown = [];
    let marketStock = 0;
    marketplaceConfig.stockColumns.forEach(col => {
      const val = Number(product[col.key]) || 0;
      marketStock += val;
      breakdown.push({ label: col.label || col.key, value: val });
    });
    const label = marketplaceConfig.daysSuppliedKey || 'Marketplace';
    return _buildDaysData(product, marketStock, breakdown, `Days Supply — ${label}`);
  }, [marketplaceConfig, _buildDaysData]);

  // Days Supply (WH + marketplace, no plan)
  const getTotalStockDaysData = useCallback((product) => {
    const wh = Number(product.whStock) || 0;
    const breakdown = [{ label: 'Warehouse (WH)', value: wh }];
    let marketStock = 0;
    marketplaceConfig.stockColumns.forEach(col => {
      const val = Number(product[col.key]) || 0;
      marketStock += val;
      breakdown.push({ label: col.label || col.key, value: val });
    });
    return _buildDaysData(product, wh + marketStock, breakdown, 'Days Supply — Total');
  }, [marketplaceConfig, _buildDaysData]);

  // Days Supply (Total + Plan)
  const getTotalPlanDaysData = useCallback((product) => {
    const wh = Number(product.whStock) || 0;
    const breakdown = [{ label: 'Warehouse (WH)', value: wh }];
    let marketStock = 0;
    marketplaceConfig.stockColumns.forEach(col => {
      const val = Number(product[col.key]) || 0;
      marketStock += val;
      breakdown.push({ label: col.label || col.key, value: val });
    });
    const planTotal = Object.values(monthlyPlanData[product.sku] || {})
      .reduce((sum, qty) => sum + (Number(qty) || 0), 0);
    if (planTotal > 0) breakdown.push({ label: 'Plan to Produce (total)', value: planTotal, isAddition: true });
    return _buildDaysData(product, wh + marketStock + planTotal, breakdown, 'Days Supply — Total + Plan');
  }, [monthlyPlanData, marketplaceConfig, _buildDaysData]);

  // Тултип для breakdown ячейки — месячный, с rolling balance
  const getMonthlyPlanTooltip = useCallback((product, planMonth, daysSupply) => {
    const getForecast = makeMonthlyForecastGetter(product);
    const curInfo     = getCurrentMonthInfo();
    const { totalStock, wh, marketStock, planQty, prevBalance, planThisMonth } =
      _stockWithCumulativePlan(product, planMonth.key, getForecast);
    const identifier   = product[identifierField];
    const marketLabels = marketplaceConfig.stockColumns.map(col => {
      const val = Number(product[col.key]) || 0;
      return `  ${col.label || col.key}: ${val.toLocaleString()}`;
    });

    const isFuture  = isFuturePlanMonth(planMonth, curInfo);
    const startInfo = isFuture ? { month: planMonth.month_num, year: planMonth.year } : curInfo;
    const startDay  = isFuture ? 1 : null;

    return getMonthlyDaysSuppliedTooltip(
      totalStock, identifier, startInfo, getForecast,
      { wh, marketStock, planQty, marketLabels, prevBalance, planThisMonth },
      daysSupply,
      startDay
    );
  }, [_stockWithCumulativePlan, identifierField, makeMonthlyForecastGetter, marketplaceConfig]);

  // Days Supply без плана — для колонок Days Supplied (amazon) и Days Supplied (Total)
  const calculateMonthlyDaysSupplied = useCallback((product, stockValue) => {
    const identifier  = product[identifierField];
    const getForecast = makeMonthlyForecastGetter(product);
    return calculateDaysSuppliedMonthly(stockValue, identifier, getCurrentMonthInfo(), getForecast);
  }, [identifierField, makeMonthlyForecastGetter]);


  // --------------------------------------------------------------
  // --------------------------------------------------------------
  const applyStandardHistoricalWeeks = (weeks) => {
    setHistoricalWeeksCount(weeks);
    loadSalesDataForMarketplace(activeMarketplace, weeks, forecastWeeksCount);
  };

  const applyCustomHistoricalWeeks = () => {
    const weeks = parseInt(customHistoricalWeeks, 10);
    if (weeks >= 1 && weeks <= 52) {
      setHistoricalWeeksCount(weeks);
      loadSalesDataForMarketplace(activeMarketplace, weeks, forecastWeeksCount);
      setCustomHistoricalWeeks('');
    }
  };

  const applyStandardForecastWeeks = (weeks) => {
    setForecastWeeksCount(weeks);
    loadSalesDataForMarketplace(activeMarketplace, historicalWeeksCount, weeks);
  };

  const applyCustomForecastWeeks = () => {
    const weeks = parseInt(customForecastWeeks, 10);
    if (weeks >= 1 && weeks <= 52) {
      setForecastWeeksCount(weeks);
      loadSalesDataForMarketplace(activeMarketplace, historicalWeeksCount, weeks);
      setCustomForecastWeeks('');
    }
  };

  // --------------------------------------------------------------
  // TABLE EXPANSION / COLUMN TOGGLES
  // --------------------------------------------------------------
  const toggleAllPreviousYears = () => {
    setShowAllPreviousYears(!showAllPreviousYears);
    if (!showAllPreviousYears) {
      const allExpanded = {};
      productsData.forEach(product => {
        allExpanded[product.id] = true;
      });
      setExpandedRows(allExpanded);
    } else {
      setExpandedRows({});
    }
  };

  const toggleColumn = (column) => {
    setVisibleColumns(prev => ({ ...prev, [column]: !prev[column] }));
  };

  // --------------------------------------------------------------
  // FILTERS
  // --------------------------------------------------------------
  const handleBrandFilterChange = (brandID) => {
    setSelectedBrands(prev => 
      prev.includes(brandID) ? prev.filter(id => id !== brandID) : [...prev, brandID]
    );
  };

  const handleNicheFilterChange = (nicheId) => {
    setSelectedNiches(prev => 
      prev.includes(nicheId) ? prev.filter(id => id !== nicheId) : [...prev, nicheId]
    );
  };

  const handleProductTypeFilterChange = (productType) => {
    setSelectedProductTypes(prev => 
      prev.includes(productType) ? prev.filter(type => type !== productType) : [...prev, productType]
    );
  };

  const clearAllFilters = () => {
    setSelectedBrands([]);
    setSelectedNiches([]);
    setSelectedProductTypes([]);
  };

  const clearBrandFilter = () => setSelectedBrands([]);
  const clearNicheFilter = () => setSelectedNiches([]);
  const clearProductTypeFilter = () => setSelectedProductTypes([]);

  // --------------------------------------------------------------
  // FILTERED PRODUCTS (with marketplace filter)
  // --------------------------------------------------------------
  const filteredProducts = useMemo(() => {
    if (!productsData || !Array.isArray(productsData)) return [];
    const query = searchQuery.toLowerCase();
    return productsData.filter(product => {
      if (!product) return false;
      // Must have valid identifier for active marketplace
      if (!marketplaceConfig.productFilter(product)) return false;
      const productName = product.productName || '';
      const identifier = product[identifierField] || '';
      const matchesSearch = productName.toLowerCase().includes(query) || identifier.toLowerCase().includes(query);
      const matchesBrand = selectedBrands.length === 0 || selectedBrands.includes(product.brandID);
      const matchesNiche = selectedNiches.length === 0 ||
        (product.niche_category_id && selectedNiches.includes(product.niche_category_id.toString())) ||
        (!product.niche_category_id && selectedNiches.includes('none'));
      const matchesProductType = selectedProductTypes.length === 0 || selectedProductTypes.includes(product.source);
      return matchesSearch && matchesBrand && matchesNiche && matchesProductType;
    });
  }, [productsData, searchQuery, selectedBrands, selectedNiches, selectedProductTypes, marketplaceConfig, identifierField]);

  // Кешируем avgGrowth для всех отфильтрованных продуктов.
  // Без этого calculateAverageGrowthForProduct вызывается при каждом рендере
  // для каждого продукта, что при 100+ продуктах генерирует тысячи вычислений.
  // ВАЖНО: должен быть объявлен ПОСЛЕ filteredProducts.
  const avgGrowthByIdentifier = useMemo(() => {
    const map = {};
    filteredProducts.forEach(product => {
      const identifier = product[identifierField];
      if (identifier) {
        map[identifier] = calculateAverageGrowthForProduct(identifier, activeMarketplace);
      }
    });
    return map;
  }, [filteredProducts, identifierField, activeMarketplace, activeSalesData, historicalWeeks]);

  // --------------------------------------------------------------
  // EDIT POPUP
  // --------------------------------------------------------------
  const openEditPopup = async (product, weekNumber, year, currentValue, field, options = {}) => {
    const marketplace = activeMarketplace;
    const config = MARKETPLACE_CONFIGS[marketplace] ?? MARKETPLACE_CONFIGS['amazon'];
    if (!config.endpoints.updateSales) {
      alert('Editing not supported for this marketplace');
      return;
    }

    const identifier = product[config.identifierField];
    let bestSeller = false,
      lightningDeal = false,
      newProduct = false,
      outOfStock = false;

    if (config.supportsFlags) {
      const salesRecord = getSalesDataFull(identifier, year, weekNumber);
      if (salesRecord) {
        bestSeller = salesRecord.best_seller === 1;
        lightningDeal = salesRecord.lightning_deal === 1;
        newProduct = salesRecord.new_product === 1;
        outOfStock = salesRecord.out_of_stock === 1;
      }
    }

    // Ручная правка числа продаж отключена — попап показывает и «сохраняет»
    // только флаги. В недельном виде значение берём из сохранённой записи
    // (переданный currentValue у current-week ячейки — это прогноз, не факт);
    // пустая строка = записи нет, флаги ставить не на что.
    if (options.mode !== 'monthly') {
      const rec = getSalesDataFull(identifier, year, weekNumber);
      currentValue = typeof rec?.units === 'number' ? rec.units : 'N/A';
    }

    setEditPopup({
      isOpen: true,
      product,
      week: weekNumber,
      year,
      currentValue: currentValue === 'N/A' ? '' : currentValue.toString(),
      field,
      showSuccessMessage: false,
      showErrorMessage: null,
      bestSeller,
      lightningDeal,
      newProduct,
      outOfStock,
      marketplace,
      supportsFlags: config.supportsFlags,
      // Monthly mode overrides — passed from YoyMonthlyView
      ...(options.mode === 'monthly' ? {
        mode:                  'monthly',
        month:                 options.month ?? weekNumber,
        bestSeller:            options.bestSeller            ?? bestSeller,
        lightningDeal:         options.lightningDeal         ?? lightningDeal,
        newProduct:            options.newProduct            ?? newProduct,
        outOfStock:            options.outOfStock            ?? outOfStock,
        supportsFlags:         options.supportsFlags         ?? config.supportsFlags,
        medianEndpoint:        options.medianEndpoint        ?? null,
        medianIdentifierParam: options.medianIdentifierParam ?? config.endpoints?.medianIdentifierParam ?? 'sku',
      } : {}),
    });
  };

  // Сохраняет ТОЛЬКО флаги: ручная правка числа продаж отключена. Количество
  // в payload не шлём вовсе — сервер обновляет флаги частично (см. память об
  // инциденте bulk edit: полный апсерт затирал бы свежие units устаревшими).
  const handleSaveEdit = async (serverMedian = null) => {
  const isMonthly = editPopup.mode === 'monthly';

  if (
    !editPopup.product ||
    !editPopup.supportsFlags ||
    (!isMonthly && !editPopup.week) ||
    (isMonthly && !editPopup.month) ||
    !editPopup.year
  )
    return;

  const marketplace = editPopup.marketplace || activeMarketplace;
  const config = MARKETPLACE_CONFIGS[marketplace] ?? MARKETPLACE_CONFIGS['amazon'];
  const endpoint = isMonthly
    ? config.endpoints.updateMonthSales
    : config.endpoints.updateSales;

  if (!endpoint) {
    setEditPopup((prev) => ({ ...prev, showErrorMessage: 'Update endpoint not configured' }));
    return;
  }

  const monthIdParam = isMonthly
    ? (config.updateMonthIdentifierParam || config.updateIdentifierParam)
    : config.updateIdentifierParam;
  const identifier = editPopup.product[config.identifierField];

  // Units берём из уже загруженной записи — флагам нужна существующая строка
  const existingRec = isMonthly
    ? (monthlySalesDataByMarketplace[marketplace] || {})[
        `${identifier}-${editPopup.year}-${String(editPopup.month).padStart(2, '0')}`]
    : getSalesDataFull(identifier, editPopup.year, editPopup.week);
  if (typeof existingRec?.units !== 'number') {
    setEditPopup((prev) => ({ ...prev,
      showErrorMessage: 'No sales record for this period yet — flags can be set once sales data exists.' }));
    return;
  }

  setLoadingEdit(true);
  try {
    const payload = {
      [isMonthly ? monthIdParam : config.updateIdentifierParam]: identifier,
      ...(isMonthly
        ? { month: parseInt(editPopup.month) }
        : { weeknumber: parseInt(editPopup.week) }),
      year: parseInt(editPopup.year),
      best_seller:    editPopup.bestSeller    ? 1 : 0,
      lightning_deal: editPopup.lightningDeal ? 1 : 0,
      new_product:    editPopup.newProduct    ? 1 : 0,
      out_of_stock:   editPopup.outOfStock    ? 1 : 0,
    };

    await api.post(endpoint, payload);

    {
      const key = isMonthly
        ? `${identifier}-${editPopup.year}-${String(editPopup.month).padStart(2, '0')}`
        : `${identifier}-${editPopup.year}-W${editPopup.week}`;

      const hasFlag =
        (isMonthly || config.supportsFlags) &&
        (editPopup.bestSeller ||
          editPopup.lightningDeal ||
          editPopup.newProduct ||
          editPopup.outOfStock);

      const units = existingRec.units; // число не редактируется — остаётся как было

      const newRecord = {
        value:          units,
        units:          units,
        hasFlag:        hasFlag ? 1 : 0,
        has_flag:       hasFlag ? 1 : 0,
        best_seller:    editPopup.bestSeller    ? 1 : 0,
        lightning_deal: editPopup.lightningDeal ? 1 : 0,
        new_product:    editPopup.newProduct    ? 1 : 0,
        out_of_stock:   editPopup.outOfStock    ? 1 : 0,
      };

      if (hasFlag) {
        if (serverMedian !== null) {
          newRecord.value       = serverMedian;
          newRecord.medianValue = serverMedian;
        } else {
          // Fallback: client-side median from loaded data
          if (isMonthly) {
            const currentMonthMap = monthlySalesDataByMarketplace[marketplace] || {};
            // N ПОСЛЕДНИХ чистых месяцев по дате (как loader и серверные
            // median-эндпоинты), медиана — уже по значениям этих месяцев
            const cleanEntries = Object.entries(currentMonthMap)
              .map(([k, r]) => {
                if (r.has_flag || r.hasFlag) return null;
                const m = k.match(/^(.+)-(\d+)-(\d+)$/);
                if (!m || m[1] !== identifier) return null;
                const y = parseInt(m[2], 10);
                const mo = parseInt(m[3], 10);
                if (y > parseInt(editPopup.year, 10)) return null;
                if (y === parseInt(editPopup.year, 10) && mo >= parseInt(editPopup.month, 10)) return null;
                if (typeof r.units !== 'number') return null;
                return { y, mo, units: r.units };
              })
              .filter(Boolean)
              .sort((a, b) => b.y - a.y || b.mo - a.mo); // самые свежие первыми
            if (cleanEntries.length > 0) {
              const slice = cleanEntries.slice(0, medianWeeksCount)
                .map(e => e.units)
                .sort((a, b) => a - b);
              const mid = Math.floor(slice.length / 2);
              const median = slice.length % 2 === 0
                ? Math.round((slice[mid - 1] + slice[mid]) / 2)
                : slice[mid];
              newRecord.value       = median;
              newRecord.medianValue = median;
            }
          } else {
            const currentSalesMap = salesDataByMarketplace[marketplace] || {};
            const editKey = `${identifier}-${editPopup.year}-W${editPopup.week}`;
            const indexedByAsin = { [identifier]: [] };
            const entryRegex = /^(.+)-(\d+)-W(\d+)$/;
            Object.entries(currentSalesMap).forEach(([k, r]) => {
              if (r.hasFlag) return;
              if (k === editKey) return;
              const m = k.match(entryRegex);
              if (!m || m[1] !== identifier) return;
              if (typeof r.units !== 'number') return;
              indexedByAsin[identifier].push({
                y: parseInt(m[2], 10),
                w: parseInt(m[3], 10),
                units: r.units,
              });
            });
            const median = computeMedianFromData(
              indexedByAsin, identifier,
              parseInt(editPopup.week, 10), parseInt(editPopup.year, 10),
              medianWeeksCount
            );
            if (median !== null) {
              newRecord.value       = median;
              newRecord.medianValue = median;
            }
          }
        }
      }

      if (isMonthly) {
        setMonthlySalesDataByMarketplace((prev) => {
          const data = { ...(prev[marketplace] || {}) };
          data[key] = newRecord;
          return { ...prev, [marketplace]: data };
        });
      } else {
        setSalesDataByMarketplace((prev) => {
          const marketplaceData = { ...(prev[marketplace] || {}) };
          marketplaceData[key] = newRecord;
          return { ...prev, [marketplace]: marketplaceData };
        });
      }

      clearSeasonalityForecastCache();

      setEditPopup((prev) => ({ ...prev, showSuccessMessage: true }));
      setTimeout(() => {
        closeEditPopup();
      }, 2000);
    }
  } catch (error) {
    if (error.response) {
      setEditPopup((prev) => ({ ...prev, showErrorMessage: error.response.data?.error || 'Update failed' }));
    } else {
      console.error('Error updating value:', error);
      setEditPopup((prev) => ({ ...prev, showErrorMessage: 'Error updating value. Please try again.' }));
    }
  } finally {
    setLoadingEdit(false);
  }
};

  const closeEditPopup = () => {
    setEditPopup({
      isOpen: false,
      product: null,
      week: null,
      year: null,
      currentValue: '',
      field: null,
      showSuccessMessage: false,
      showErrorMessage: null,
      bestSeller: false,
      lightningDeal: false,
      newProduct: false,
      outOfStock: false,
      marketplace: null,
      supportsFlags: false,
    });
  };
  // --------------------------------------------------------------
  // UPLOAD HANDLERS (Sales)
  // --------------------------------------------------------------
  // STOCK UPLOAD (Amazon only)
  // --------------------------------------------------------------
  const handleStockFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setSelectedStockFile(file);
    setParsedStockData([]);
    setUploadStockStatus(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      const firstRow = jsonData[0] || {};
      const hasASIN = 'ASIN' in firstRow || 'asin' in firstRow || 'Asin' in firstRow;
      if (!hasASIN) {
        setUploadStockStatus({ type: 'error', message: 'File must contain ASIN column' });
        return;
      }
      const aggregatedData = aggregateStockDataFrontend(jsonData);
      setParsedStockData(aggregatedData);
    };
    reader.onerror = () => setUploadStockStatus({ type: 'error', message: 'Error reading file' });
    reader.readAsArrayBuffer(file);
  };

  const aggregateStockDataFrontend = (data) => {
    const asinMap = new Map();
    data.forEach(row => {
      const asin = row.ASIN || row.asin || row.Asin || '';
      if (!asin || asin.trim() === '') return;
      const available = parseInt(row.available || row.Available || row['available '] || 0) || 0;
      const inboundQuantity = parseInt(row['inbound-quantity'] || row['inbound-quantity '] || row.inbound_quantity || row['Inbound Quantity'] || 0) || 0;
      const reservedFCTransfer = parseInt(row['fc-transfer'] || row['Reserved FC Transfer'] || row['Reserved FC Transfer '] || row['reserved-fc-transfer'] || row['Reserved FC transfer'] || 0) || 0;
      const reservedFCProcessing = parseInt(row['Reserved FC Processing'] || row['Reserved FC Processing '] || row['reserved-fc-processing'] || row['Reserved FC processing'] || 0) || 0;
      if (asinMap.has(asin)) {
        const existing = asinMap.get(asin);
        asinMap.set(asin, {
          available: existing.available + available,
          inbound_quantity: existing.inbound_quantity + inboundQuantity,
          reserved_fc_transfer: existing.reserved_fc_transfer + reservedFCTransfer,
          reserved_fc_processing: existing.reserved_fc_processing + reservedFCProcessing,
          total_quantity: existing.available + existing.inbound_quantity + existing.reserved_fc_transfer + existing.reserved_fc_processing
        });
      } else {
        asinMap.set(asin, {
          available,
          inbound_quantity: inboundQuantity,
          reserved_fc_transfer: reservedFCTransfer,
          reserved_fc_processing: reservedFCProcessing,
          total_quantity: available + inboundQuantity + reservedFCTransfer + reservedFCProcessing
        });
      }
    });
    return Array.from(asinMap.entries()).map(([asin, values]) => ({
      id: asin,
      asin,
      available: values.available,
      inbound_quantity: values.inbound_quantity,
      reserved_fc_transfer: values.reserved_fc_transfer,
      reserved_fc_processing: values.reserved_fc_processing,
      total_quantity: values.total_quantity
    }));
  };

  const handleStockUploadSubmit = async () => {
    if (activeMarketplace !== 'amazon') {
      setUploadStockStatus({ type: 'error', message: 'Stock upload is only available for Amazon' });
      return;
    }
    if (!selectedStockFile) {
      setUploadStockStatus({ type: 'error', message: 'Please select a file' });
      return;
    }
    if (parsedStockData.length === 0) {
      setUploadStockStatus({ type: 'error', message: 'No valid data found in the file' });
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedStockFile);

    try {
      setUploadStockStatus({ type: 'loading', message: 'Uploading stock data...' });
      const { data: result } = await api.post(`/api/upload-fba-stock`, formData);
      setUploadStockStatus({ type: 'success', message: result.message || 'Stock data uploaded successfully' });
      loadProducts(); // Reload products to get updated FBA stock
      setTimeout(() => {
        setShowStockUploadPopup(false);
        setSelectedStockFile(null);
        setParsedStockData([]);
        setUploadStockStatus(null);
        if (stockFileInputRef.current) stockFileInputRef.current.value = '';
      }, 3000);
    } catch (error) {
      if (error.response) {
        setUploadStockStatus({ type: 'error', message: error.response.data?.error || 'Upload failed' });
      } else {
        setUploadStockStatus({ type: 'error', message: `Error: ${error.message}` });
      }
    }
  };

  // --------------------------------------------------------------
  // WRAPPER FUNCTIONS (for days supplied calculations, etc.)
  // --------------------------------------------------------------

  const getWeeksBetweenWrapper = (startWeek, endWeek) => {
    return getWeeksBetween(startWeek, endWeek);
  };

  const getInitialStockForMarketplace = useCallback((product, marketplace) => {
    const config = MARKETPLACE_CONFIGS[marketplace] ?? MARKETPLACE_CONFIGS['amazon'];
    switch (marketplace) {
      case 'amazon':
        return (product.fbaStock || 0) + (product.awdStock || 0);
      case 'walmart':
        return product.wmStock || 0;
      default: // shopify, etsy, etc.
        return product.whStock || 0;
    }
  }, []);

  const getStockRemainingAfterWeeksForMarketplace = useCallback((
  product,
  targetWeek,
  targetYear,
  marketplace = activeMarketplace
) => {
  if (!product) return 0;

  const initialStock = getInitialStockForMarketplace(product, marketplace);
  const identifier = product[identifierField];

  return calculateStockRemainingAfterWeeks(
    initialStock,
    identifier,
    targetWeek,
    targetYear,
    currentWeekInfo,
    (id, week, year, growth) =>
      calculateForecastForProductConditional(product, week, year, growth, marketplace),
    (id) => calculateAverageGrowthForProduct(id, marketplace),
    getWeeksBetweenWrapper
  );
}, [productsData, currentWeekInfo, identifierField, getInitialStockForMarketplace,
    calculateForecastForProductConditional, calculateAverageGrowthForProduct,
    getWeeksBetweenWrapper, activeMarketplace]);

  const calculateDaysSuppliedAtWeekForMarketplace = useCallback((
    product,
    targetWeek,
    targetYear,
    marketplace = activeMarketplace
  ) => {
    if (!product) return 0;

    const stockAtWeekStart = getStockRemainingAfterWeeksForMarketplace(
      product,
      targetWeek,
      targetYear,
      marketplace
    );
    if (stockAtWeekStart <= 0) return 0;

    const identifier = product[identifierField];

    return calculateDaysSuppliedFromWeek(
      stockAtWeekStart,
      identifier,
      targetWeek,
      targetYear,
      currentWeekInfo,
      (id) => calculateAverageGrowthForProduct(id, marketplace),
      (id, week, year, growth) =>
        calculateForecastForProductConditional(product, week, year, growth, marketplace),
      DAYS_SUPPLIED_CALCULATION_WEEKS
    );
  }, [getStockRemainingAfterWeeksForMarketplace, currentWeekInfo,
      identifierField, calculateAverageGrowthForProduct,
      calculateForecastForProductConditional, activeMarketplace]);
    
    const getForecastCellClassForMarketplace = useCallback((
      identifier,
      weekNumber,
      year,
      marketplace = activeMarketplace,
      productArg = null
    ) => {
      // Ячейки таблицы передают product сами (иначе O(N)-поиск на каждую из
      // ~52 прогнозных ячеек каждой строки заметно тормозил перерисовку)
      const product = productArg || productsData.find(p => p[identifierField] === identifier);
      if (!product) return 'weeks-supplied-red';

      return getForecastCellClass(
        identifier,
        weekNumber,
        year,
        (asin, w, y) => calculateDaysSuppliedAtWeekForMarketplace(product, w, y, marketplace)
      );
    }, [productsData, identifierField, calculateDaysSuppliedAtWeekForMarketplace, activeMarketplace]);

  const getCurrentWeekColorForMarketplace = useCallback((product, marketplace = activeMarketplace) => {
    const identifier = product[identifierField];
    const daysData = activeDaysSuppliedData[identifier] || {};

    let daysSupplied;
    if (marketplace === 'amazon') {
      daysSupplied = daysData.amazon || 0;
    } else if (marketplace === 'walmart') {
      daysSupplied = daysData.walmart || 0;
    } else {
      // For Shopify/Etsy use total days supplied (WH stock only)
      daysSupplied = daysData.total || 0;
    }

    const weeksCovered = daysSupplied / 7;
    if (weeksCovered >= 16) return 'weeks-supplied-purple';
    if (weeksCovered >= 10) return 'weeks-supplied-green';
    if (weeksCovered >= 4) return 'weeks-supplied-yellow';
    if (weeksCovered > 0) return 'weeks-supplied-orange';
    return 'weeks-supplied-red';
  }, [activeDaysSuppliedData, identifierField, activeMarketplace]);

  const calculateStockAfterWeeksWrapper = (asin, targetWeek, targetYear) => {
    // Only used for Amazon? Keep as is.
    return calculateStockAfterWeeks(
      asin,
      targetWeek,
      targetYear,
      currentWeekInfo,
      (id) => calculateAverageGrowthForProduct(id, 'amazon'),
      (id, week, year, growth) => calculateForecastForProduct(id, week, year, growth, 'amazon'),
      getWeeksBetweenWrapper
    );
  };
  
  const calculateTotalStockAfterWeeksWrapper = (asin, targetWeek, targetYear) => {
    const product = productsData.find(p => p.asin === asin);
    if (!product) return 0;
    let remainingStock = (product.fbaStock || 0) + (product.awdStock || 0) + (product.whStock || 0);
    if (targetWeek === currentWeekInfo?.weekNumber && targetYear === currentWeekInfo?.year) return remainingStock;
    const averageGrowth = calculateAverageGrowthForProduct(asin, 'amazon');
    const weeksToSubtract = getWeeksBetweenWrapper(
      { weekNumber: currentWeekInfo.weekNumber, year: currentWeekInfo.year },
      { weekNumber: targetWeek, year: targetYear }
    );
    weeksToSubtract.forEach(week => {
      const forecast = calculateForecastForProduct(asin, week.weekNumber, week.year, averageGrowth, 'amazon');
      if (forecast !== 'N/A' && forecast > 0) {
        remainingStock -= forecast;
        if (remainingStock < 0) remainingStock = 0;
      }
    });
    return remainingStock;
  };

  const calculateDaysSuppliedFromWeekWrapper = (stock, asin, startWeek, startYear) => {
    return calculateDaysSuppliedFromWeek(
      stock,
      asin,
      startWeek,
      startYear,
      currentWeekInfo,
      (id) => calculateAverageGrowthForProduct(id, 'amazon'),
      (id, week, year, growth) => calculateForecastForProduct(id, week, year, growth, 'amazon'),
      DAYS_SUPPLIED_CALCULATION_WEEKS
    );
  };

  const calculateColorByWeeksWrapper = (remainingStock, asin) => {
    return calculateColorByWeeks(
      remainingStock,
      asin,
      currentWeekInfo,
      (id) => calculateAverageGrowthForProduct(id, 'amazon'),
      (id, week, year, growth) => calculateForecastForProduct(id, week, year, growth, 'amazon')
    );
  };

  const getExtendedForecastWeeksWrapper = (startWeek, startYear, count) => {
    return getExtendedForecastWeeks(startWeek, startYear, count, getDateOfISOWeek);
  };

  // --------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------
  const overallLoading = loadingProducts || loadingSales;

  // Объединённые исторические ячейки (rowSpan=2 накрывает строку ИИ, сетка
  // факт/наш прогноз/ИИ) живут вместе со строкой Agentic — условия обязаны
  // совпадать с условием рендера самой строки
  const aiMergedOn = showAgenticRow && activeMarketplace === 'amazon' && !!currentWeekInfo;

  return (
    <>
      <HeaderLogged />
      <div className="yoy-container">
        <h1>YOY Forecast</h1>

                {/* View mode toggle */}
        <div className="view-mode-tabs">
          <button
            className={`view-mode-tab ${viewMode === 'weekly' ? 'active' : ''}`}
            onClick={() => setViewMode('weekly')}
          >
            By Week
          </button>
          <button
            className={`view-mode-tab ${viewMode === 'monthly' ? 'active' : ''}`}
            onClick={() => setViewMode('monthly')}
          >
            By Month
          </button>
        </div>

        <YoyAdminPanel
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          showStockUploadPopup={showStockUploadPopup}
          setShowStockUploadPopup={setShowStockUploadPopup}
          brandsData={brandsData}
          nichesData={nichesData}
          selectedBrands={selectedBrands}
          setSelectedBrands={setSelectedBrands}
          selectedNiches={selectedNiches}
          setSelectedNiches={setSelectedNiches}
          selectedProductTypes={selectedProductTypes}
          setSelectedProductTypes={setSelectedProductTypes}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
          showAllPreviousYears={showAllPreviousYears}
          setShowAllPreviousYears={setShowAllPreviousYears}
          historicalWeeksCount={historicalWeeksCount}
          setHistoricalWeeksCount={setHistoricalWeeksCount}
          forecastWeeksCount={forecastWeeksCount}
          setForecastWeeksCount={setForecastWeeksCount}
          customHistoricalWeeks={customHistoricalWeeks}
          setCustomHistoricalWeeks={setCustomHistoricalWeeks}
          customForecastWeeks={customForecastWeeks}
          setCustomForecastWeeks={setCustomForecastWeeks}
          loadProducts={loadProducts}
          loadSalesData={loadSalesData}
          applyStandardHistoricalWeeks={applyStandardHistoricalWeeks}
          applyCustomHistoricalWeeks={applyCustomHistoricalWeeks}
          applyStandardForecastWeeks={applyStandardForecastWeeks}
          applyCustomForecastWeeks={applyCustomForecastWeeks}
          toggleColumn={toggleColumn}
          toggleAllPreviousYears={toggleAllPreviousYears}
          showStockBalanceRow={showStockBalanceRow}
          toggleStockBalanceRow={toggleStockBalanceRow}
          showAgenticRow={showAgenticRow}
          toggleAgenticRow={toggleAgenticRow}
          onOpenAiHistory={() => { setAiHistory(h => ({ ...h, open: true })); loadAiVersions(aiHistory.period); }}
          handleBrandFilterChange={handleBrandFilterChange}
          handleNicheFilterChange={handleNicheFilterChange}
          handleProductTypeFilterChange={handleProductTypeFilterChange}
          clearAllFilters={clearAllFilters}
          clearBrandFilter={clearBrandFilter}
          clearNicheFilter={clearNicheFilter}
          clearProductTypeFilter={clearProductTypeFilter}
          productsData={productsData}
          filteredProducts={filteredProducts}
          overallLoading={overallLoading}
          activeMarketplace={activeMarketplace}
          marketplaceConfig={marketplaceConfig}
          medianWeeksCount={medianWeeksCount}
          setMedianWeeksCount={setMedianWeeksCount}
          viewMode={viewMode}
          historicalMonthsCount={historicalMonthsCount}
          setHistoricalMonthsCount={setHistoricalMonthsCount}
          forecastMonthsCount={forecastMonthsCount}
          setForecastMonthsCount={setForecastMonthsCount}
          medianMonthsCount={medianMonthsCount}
          setMedianMonthsCount={setMedianMonthsCount}
        />

        <MarketplaceTabs
          activeMarketplace={activeMarketplace}
          onMarketplaceChange={setActiveMarketplace}
        />

        {overallLoading && (
          <div className="loading-container">
            <div className="loading-indicator">
              <div className="spinner"></div>
              <p>Loading {activeMarketplace} data...</p>
            </div>
          </div>
        )}

        {!loadingProducts && filteredProducts.length > 0 && viewMode === 'weekly' && activeMarketplace !== 'summary' && (
          <div className="table-responsive">
            <table className="yoy-table">
              <thead>
                <tr>
                  <th rowSpan="2" className="checkbox-fixed-column">
                    <div>Seasonality</div>
                  </th>

                  <th rowSpan="2" className="yoy-fixed-column product-name-head">Product Name</th>
                  <th rowSpan="2">{identifierField.toUpperCase()}</th>
                  <th rowSpan="2">Bags per box</th>
                  <th rowSpan="2">WH Stock</th>

                  {/* Marketplace-specific stock columns */}
                  {marketplaceConfig.stockColumns.map(col => (
                    <th key={col.key} rowSpan="2">{col.label}</th>
                  ))}

                  <th rowSpan="2">Box can be shipped</th>

                  {/* Days Supplied columns */}
                  {marketplaceConfig.daysSuppliedKey && (
                    <th rowSpan="2">Days Supplied ({marketplaceConfig.daysSuppliedKey})</th>
                  )}
                  <th rowSpan="2">Days Supplied (Total)</th>
                  <th rowSpan="2">Due Date</th>
                  <th rowSpan="2">
                    <span>Plan to Produce</span>
                  </th>
                  <th rowSpan="2" style={{ whiteSpace: 'nowrap' }}>Days Supply (Total + Plan)</th>

                  {/* Historical columns */}
                  {visibleColumns.historical && historicalWeeks.length > 0 && (
                    <th colSpan={historicalWeeks.length} className="section-header">
                      Historical Data ({historicalWeeksCount} weeks)
                    </th>
                  )}

                  {/* Current week column */}
                  {visibleColumns.currentWeek && currentWeekInfo && (
                    <th rowSpan="2" className="current-week-header section-header">
                      Current Week (W{currentWeekInfo.weekNumber} {currentWeekInfo.year})
                      <div style={{ fontSize: '10px', fontWeight: 'normal' }}>
                        {currentWeekInfo.mondayDate} - {currentWeekInfo.sundayDate}
                      </div>
                    </th>
                  )}

                  {/* Prediction columns */}
                  {visibleColumns.prediction && forecastWeeks.length > 0 && (
                    <th colSpan={forecastWeeks.length} className="section-header">
                      Prediction ({forecastWeeksCount} weeks)
                    </th>
                  )}

                  
                </tr>

                <tr>
                  {/* Subheaders for historical weeks */}
                  {visibleColumns.historical && historicalWeeks.map((week, idx) => (
                    <th key={idx} className="week-header">
                      <div>W{week.weekNumber} {week.year}</div>
                      <div style={{ fontSize: '9px', fontWeight: 'normal' }}>{week.dateRange}</div>
                    </th>
                  ))}

                  {/* Subheaders for prediction weeks */}
                  {visibleColumns.prediction && forecastWeeks.map((week, idx) => (
                    <th key={idx} className="prediction-header">
                      W{week.weekNumber} {week.year}
                    </th>
                  ))}


                </tr>
              </thead>

              <tbody>
                {filteredProducts.map(product => {
                  const identifier = product[identifierField];
                  const isExpanded = expandedRows[product.id];
                  const boxCanBeShipped = calculateBoxCanBeShipped(product.whStock, product.bagsPerBox);
                  const avgGrowth = avgGrowthByIdentifier[identifier] ?? calculateAverageGrowthForProduct(identifier, activeMarketplace);
                  const daysData = activeDaysSuppliedData[identifier] || {};

                  return (
                    <React.Fragment key={product.id}>
                      {/* Previous Year Row (if expanded) */}
                      {isExpanded && (
                        <tr className="previous-year-row">
                          {/* Seasonality checkbox (rowSpan)*/}
                            <td rowSpan={aiMergedOn ? 3 : 2} className="checkbox-fixed-column">
                                {product.niche_category_id ? (
                                  <input
                                    type="checkbox"
                                    checked={seasonalityFlags[product.asin] || false}
                                    onChange={(e) => toggleSeasonalityFlag(product.asin, e.target.checked)}
                                  />
                                ) : (
                                  <span title="No niche assigned" style={{ color: '#ccc' }}>—</span>
                                )}
                            </td>
                         
                          <td rowSpan={aiMergedOn ? 3 : 2} className="yoy-fixed-column product-cell">{product.productName}</td>
                          <td rowSpan={aiMergedOn ? 3 : 2} className="identifier-cell">{identifier}</td>
                          <td rowSpan={aiMergedOn ? 3 : 2} className="bags-cell">{product.bagsPerBox}</td>
                          <td rowSpan={aiMergedOn ? 3 : 2} className="wh-stock-cell">{product.whStock}</td>

                          {/* Marketplace stock columns */}
                          {marketplaceConfig.stockColumns.map(col => (
                            <td key={col.key} rowSpan={aiMergedOn ? 3 : 2} className="stock-cell">
                              {product[col.key] ?? 0}
                            </td>
                          ))}

                          <td rowSpan={aiMergedOn ? 3 : 2} className="ship-cell">{boxCanBeShipped}</td>

                          {/* Days Supplied columns */}
                          {/* Marketplace-specific days supplied (if any) */}
                          {marketplaceConfig.daysSuppliedKey && (
                            <td rowSpan="2" className="days-supplied-market-cell previous-year">
                              {renderDaysSuppliedCell(daysData[marketplaceConfig.daysSuppliedKey] || 0)}
                            </td>
                          )}
                          {/* Total days supplied – always */}
                          <td rowSpan="2" className="days-supplied-total-cell previous-year">
                            {renderDaysSuppliedCell(daysData.total || 0)}
                          </td>
                          {/* Due Date – always, value depends on marketplace */}
                          <td rowSpan="2" className="due-date-cell previous-year">
                            {(() => {
                              const dueDateDays = marketplaceConfig.daysSuppliedKey
                                ? (daysData[marketplaceConfig.daysSuppliedKey] || 0)
                                : (daysData.total || 0);
                              return (
                                <div className={`due-date-value ${getDueDateClass(dueDateDays)}`}>
                                  {calculateDueDate(dueDateDays)}
                                </div>
                              );
                            })()}
                          </td>

                          {/* Plan to Produce */}
                          {(() => {
                            const planQty = planToProduceData[product.sku] || 0;
                            return (
                              <td rowSpan="2" className="plan-to-produce-cell previous-year">
                                {planQty > 0 ? planQty : '—'}
                              </td>
                            );
                          })()}

                          {/* Days Supply (Total + Plan to Produce) */}
                          {(() => {
                            const planQty = planToProduceData[product.sku] || 0;
                            const wh = Number(product.whStock) || 0;
                            let marketStock = 0;
                            if (marketplaceConfig.stockColumns.length > 0) {
                              marketplaceConfig.stockColumns.forEach(col => {
                                marketStock += Number(product[col.key]) || 0;
                              });
                            }
                            const totalWithPlan = wh + marketStock + planQty;
                            const forecastWithSeasonality = (id, week, year, growth) =>
                              calculateForecastForProductConditional(product, week, year, growth, activeMarketplace);
                            const salesForMarket = salesDataByMarketplace[activeMarketplace] || {};
                            const getSalesForMarket = (id, year, week) => {
                              const key = `${id}-${year}-W${week}`;
                              return salesForMarket[key]?.value ?? 'N/A';
                            };
                            const daysWithPlan = calculateDaysSupplied(
                              totalWithPlan,
                              identifier,
                              null,
                              null,
                              currentWeekInfo,
                              (id) => calculateAverageGrowthForProduct(id, activeMarketplace),
                              forecastWithSeasonality,
                              getSalesForMarket
                            );
                            return (
                              <td rowSpan="2" className="days-supplied-plan-cell previous-year">
                                {renderDaysSuppliedCell(daysWithPlan || 0)}
                              </td>
                            );
                          })()}

                          {/* Historical - Previous Year */}
                          {visibleColumns.historical && historicalWeeks.map((week, idx) => {
                            const prevYear = week.year - 1;
                            const value = getSalesData(identifier, prevYear, week.weekNumber);
                            const record = getSalesDataFull(identifier, prevYear, week.weekNumber);
                            const hasFlag = !!record?.hasFlag;
                            const isMedian = hasFlag && record?.medianValue !== undefined;
                            const flagLabel = hasFlag
                              ? record?.best_seller ? 'Best Seller'
                              : record?.lightning_deal ? 'Deal / Promo'
                              : record?.new_product ? 'New Product'
                              : record?.out_of_stock ? 'Out of Stock'
                              : 'Flag'
                              : '';
                            const flagClass = hasFlag
                              ? record?.best_seller ? 'flag-best-seller'
                              : record?.lightning_deal ? 'flag-lightning-deal'
                              : record?.new_product ? 'flag-new-product'
                              : record?.out_of_stock ? 'flag-out-of-stock'
                              : ''
                              : '';
                            const cellTitle = hasFlag
                              ? `${flagLabel}${isMedian ? ` · Median value (raw: ${record.units})` : ''}`
                              : undefined;
                            return (
                              <td
                                key={idx}
                                className={`historical-cell previous-year${hasFlag ? ' has-flag' : ''}${marketplaceConfig.supportsWeeklyFlags ? ' cell-clickable' : ''}`}
                                onClick={marketplaceConfig.supportsWeeklyFlags
                                  ? () => openEditPopup(product, week.weekNumber, prevYear, record?.units ?? value, 'previousYear')
                                  : undefined}
                              >
                                <div className={`value-with-edit${flagClass ? ' ' + flagClass : ''}`} title={cellTitle}>
                                  <span>
                                    {value}
                                    {isMedian && (
                                      <span title={`Median (replaces raw: ${record.units})`} style={{ fontSize: '10px', color: '#e67e22', fontWeight: 'bold', marginLeft: '2px', cursor: 'help' }}>~</span>
                                    )}
                                  </span>
                                </div>
                              </td>
                            );
                          })}

                          {/* Current Week - Previous Year */}
                          {visibleColumns.currentWeek && currentWeekInfo && (
                            <td
                              className={`current-week-cell previous-year${marketplaceConfig.supportsWeeklyFlags ? ' cell-clickable' : ''}`}
                              onClick={marketplaceConfig.supportsWeeklyFlags
                                ? () => openEditPopup(product, currentWeekInfo.weekNumber, currentWeekInfo.year - 1,
                                    getSalesData(identifier, currentWeekInfo.year - 1, currentWeekInfo.weekNumber), 'previousYear')
                                : undefined}
                            >
                              <div className="value-with-edit">
                                {getSalesData(identifier, currentWeekInfo.year - 1, currentWeekInfo.weekNumber)}
                              </div>
                            </td>
                          )}

                          {/* Prediction - Previous Year */}
                          {visibleColumns.prediction && forecastWeeks.map((week, idx) => {
                            const prevYear = week.year - 1;
                            const value = getSalesData(identifier, prevYear, week.weekNumber);
                            return (
                              <td
                                key={idx}
                                className={`prediction-cell previous-year${marketplaceConfig.supportsWeeklyFlags ? ' cell-clickable' : ''}`}
                                onClick={marketplaceConfig.supportsWeeklyFlags
                                  ? () => openEditPopup(product, week.weekNumber, prevYear, value, 'predictionPreviousYear')
                                  : undefined}
                              >
                                <div className="value-with-edit">
                                  {value}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      )}

                      {/* Current Year Row */}
                      <tr className="current-year-row">
                        {!isExpanded && (
                          <>
                            {/* Seasonality checkbox - Amazon only */}
                            
                              <td rowSpan={aiMergedOn ? 2 : 1} className="checkbox-fixed-column">
                                {product.niche_category_id ? (
                                  <input
                                    type="checkbox"
                                    checked={seasonalityFlags[product.asin] || false}
                                    onChange={(e) => toggleSeasonalityFlag(product.asin, e.target.checked)}
                                  />
                                ) : (
                                  <span title="No niche assigned" style={{ color: '#ccc' }}>—</span>
                                )}
                              </td>
                          

                            <td rowSpan={aiMergedOn ? 2 : 1} className="yoy-fixed-column product-cell">{product.productName}</td>
                            <td rowSpan={aiMergedOn ? 2 : 1} className="identifier-cell">{identifier}</td>
                            <td rowSpan={aiMergedOn ? 2 : 1} className="bags-cell">{product.bagsPerBox}</td>
                            <td rowSpan={aiMergedOn ? 2 : 1} className="wh-stock-cell">{product.whStock}</td>

                            {marketplaceConfig.stockColumns.map(col => (
                              <td key={col.key} rowSpan={aiMergedOn ? 2 : 1} className="stock-cell">{product[col.key] ?? 0}</td>
                            ))}

                            <td rowSpan={aiMergedOn ? 2 : 1} className="ship-cell">{boxCanBeShipped}</td>

                            {marketplaceConfig.daysSuppliedKey && (
                              <td className="days-supplied-market-cell">
                                {renderDaysSuppliedCell(daysData[marketplaceConfig.daysSuppliedKey] || 0)}
                              </td>
                            )}
                            {/* Total days supplied – always */}
                            <td className="days-supplied-total-cell">
                              {renderDaysSuppliedCell(daysData.total || 0)}
                            </td>
                            {/* Due Date – always, value depends on marketplace */}
                            <td className="due-date-cell">
                              {(() => {
                                const dueDateDays = marketplaceConfig.daysSuppliedKey
                                  ? (daysData[marketplaceConfig.daysSuppliedKey] || 0)
                                  : (daysData.total || 0);
                                return (
                                  <div className={`due-date-value ${getDueDateClass(dueDateDays)}`}>
                                    {calculateDueDate(dueDateDays)}
                                  </div>
                                );
                              })()}
                            </td>

                            {/* Plan to Produce */}
                            {(() => {
                              const planQty = planToProduceData[product.sku] || 0;
                              return (
                                <td className="plan-to-produce-cell">
                                  {planQty > 0 ? planQty : '—'}
                                </td>
                              );
                            })()}

                            {/* Days Supply (Total + Plan to Produce) */}
                            {(() => {
                              const planQty = planToProduceData[product.sku] || 0;
                              const wh = Number(product.whStock) || 0;
                              let marketStock = 0;
                              if (marketplaceConfig.stockColumns.length > 0) {
                                marketplaceConfig.stockColumns.forEach(col => {
                                  marketStock += Number(product[col.key]) || 0;
                                });
                              }
                              const totalWithPlan = wh + marketStock + planQty;
                              const forecastWithSeasonality = (id, week, year, growth) =>
                                calculateForecastForProductConditional(product, week, year, growth, activeMarketplace);
                              const salesForMarket = salesDataByMarketplace[activeMarketplace] || {};
                              const getSalesForMarket = (id, year, week) => {
                                const key = `${id}-${year}-W${week}`;
                                return salesForMarket[key]?.value ?? 'N/A';
                              };
                              const daysWithPlan = calculateDaysSupplied(
                                totalWithPlan,
                                identifier,
                                null,
                                null,
                                currentWeekInfo,
                                (id) => calculateAverageGrowthForProduct(id, activeMarketplace),
                                forecastWithSeasonality,
                                getSalesForMarket
                              );
                              return (
                                <td className="days-supplied-plan-cell">
                                  {renderDaysSuppliedCell(daysWithPlan || 0)}
                                </td>
                              );
                            })()}
                          </>
                        )}

                        {/* Historical - Current Year */}
                        {visibleColumns.historical && historicalWeeks.map((week, idx) => {
                          const currentValue = getSalesData(identifier, week.year, week.weekNumber);
                          const previousValue = getSalesData(identifier, week.year - 1, week.weekNumber);
                          const change = calculatePercentageChange(currentValue, previousValue);
                          const record = getSalesDataFull(identifier, week.year, week.weekNumber);
                          const hasFlag = !!record?.hasFlag;
                          const isMedian = hasFlag && record?.medianValue !== undefined;
                          const flagLabel = hasFlag
                            ? record?.best_seller ? 'Best Seller'
                            : record?.lightning_deal ? 'Deal / Promo'
                            : record?.new_product ? 'New Product'
                            : record?.out_of_stock ? 'Out of Stock'
                            : 'Flag'
                            : '';
                          const flagClass = hasFlag
                            ? record?.best_seller ? 'flag-best-seller'
                            : record?.lightning_deal ? 'flag-lightning-deal'
                            : record?.new_product ? 'flag-new-product'
                            : record?.out_of_stock ? 'flag-out-of-stock'
                            : ''
                            : '';
                          const cellTitle = hasFlag
                            ? `${flagLabel}${isMedian ? ` · Median value (raw: ${record.units})` : ''}`
                            : undefined;
                          const wkRange = isoWeekRangeStr(week.year, week.weekNumber);
                          const wkDeals = dealsForRange(dealsData[identifier], wkRange.start, wkRange.end);
                          // При включённом Agentic историческая ячейка объединена
                          // со строкой ИИ (rowSpan=2): сетка 3×2 — факт и %изм,
                          // ниже мельче наш прогноз и прогноз ИИ с их точностью
                          return (
                            <td
                              key={idx}
                              rowSpan={aiMergedOn ? 2 : 1}
                              className={`historical-cell${hasFlag ? ' has-flag' : ''}${aiMergedOn ? ' hist-merged' : ''}${marketplaceConfig.supportsWeeklyFlags ? ' cell-clickable' : ''}`}
                              onClick={marketplaceConfig.supportsWeeklyFlags
                                ? () => openEditPopup(product, week.weekNumber, week.year, record?.units ?? currentValue, 'historical')
                                : undefined}
                            >
                              {aiMergedOn ? (() => {
                                const k = aiWeekKey(week.year, week.weekNumber);
                                const page = (aiForecastData.week?.page?.[identifier] || {})[k];
                                const ai = (aiForecastData.week?.ai?.[identifier] || {})[k]?.value;
                                const actual = typeof currentValue === 'number' ? currentValue : null;
                                const ourAcc = forecastMatchPct(page, actual);
                                const aiAcc = forecastMatchPct(ai, actual);
                                const ourDev = forecastDeviationPct(page, actual);
                                const aiDev = forecastDeviationPct(ai, actual);
                                const aiWins = ourAcc != null && aiAcc != null && aiAcc !== ourAcc ? aiAcc > ourAcc : null;
                                return (
                                  <div className="hist-grid">
                                    <div className={`hist-sales${flagClass ? ' ' + flagClass : ''}`} title={cellTitle || 'Actual sales'}>
                                      {renderDealDot(wkDeals, setInfoPopup)}
                                      {currentValue}
                                      {isMedian && (
                                        <span title={`Median (replaces raw: ${record.units})`} style={{ fontSize: '9px', color: '#e67e22', fontWeight: 'bold', marginLeft: '2px' }}>~</span>
                                      )}
                                    </div>
                                    <div className="hist-change">
                                      {change && (
                                        <span className={`change-indicator ${change.startsWith('+') ? 'positive' : 'negative'}`}>{change}</span>
                                      )}
                                    </div>
                                    <div className="hist-sub" title="Our forecast for this week (snapshot made before the week ended)">
                                      {typeof page === 'number' ? page : '—'}
                                    </div>
                                    <div className={`hist-sub hist-acc${aiWins === false ? ' acc-win' : ''}`} title="Our forecast match vs actual sales (100% = exact hit) · signed % = deviation: minus — forecast was LOWER than actual (underplanned), plus — higher (overplanned)">
                                      {ourAcc != null ? `${ourAcc}%` : '—'}
                                      {renderDeviation(ourDev)}
                                    </div>
                                    <div className="hist-sub hist-ai" title="AI forecast (main value, submitted before the week ended)">
                                      {typeof ai === 'number' ? ai : '—'}
                                    </div>
                                    <div className={`hist-sub hist-ai hist-acc${aiWins === true ? ' acc-win' : ''}`} title="AI forecast match vs actual sales (100% = exact hit) · signed % = deviation: minus — forecast was LOWER than actual (underplanned), plus — higher (overplanned)">
                                      {aiAcc != null ? `${aiAcc}%` : '—'}
                                      {renderDeviation(aiDev)}
                                    </div>
                                  </div>
                                );
                              })() : (
                                <div className="cell-content">
                                  <div className={`value-with-edit${flagClass ? ' ' + flagClass : ''}`} title={cellTitle}>
                                    <span>
                                      {renderDealDot(wkDeals, setInfoPopup)}
                                      {currentValue}
                                      {isMedian && (
                                        <span title={`Median (replaces raw: ${record.units})`} style={{ fontSize: '10px', color: '#e67e22', fontWeight: 'bold', marginLeft: '2px', cursor: 'help' }}>~</span>
                                      )}
                                    </span>
                                  </div>
                                  {change && (
                                    <span className={`change-indicator ${change.startsWith('+') ? 'positive' : 'negative'}`}>
                                      {change}
                                    </span>
                                  )}
                                </div>
                              )}
                            </td>
                          );
                        })}

                        {/* Current Week - Forecast (+ % к прошлому году, как у прогнозных) */}
                        {visibleColumns.currentWeek && currentWeekInfo && (() => {
                          const cwForecast = calculateForecastForProductConditional(
                            product, currentWeekInfo.weekNumber, currentWeekInfo.year, avgGrowth, activeMarketplace);
                          const cwPrevious = getSalesData(identifier, currentWeekInfo.year - 1, currentWeekInfo.weekNumber);
                          const cwChange = calculatePercentageChange(cwForecast, cwPrevious);
                          const cwRange = isoWeekRangeStr(currentWeekInfo.year, currentWeekInfo.weekNumber);
                          const cwDeals = dealsForRange(dealsData[identifier], cwRange.start, cwRange.end);
                          return (
                            <td
                              className={`current-week-cell ${getCurrentWeekColorForMarketplace(product, activeMarketplace)}${marketplaceConfig.supportsWeeklyFlags ? ' cell-clickable' : ''}`}
                              onClick={marketplaceConfig.supportsWeeklyFlags
                                ? () => openEditPopup(product, currentWeekInfo.weekNumber, currentWeekInfo.year,
                                    calculateForecastForProductConditional(identifier, currentWeekInfo.weekNumber, currentWeekInfo.year, avgGrowth), 'currentWeek')
                                : undefined}
                            >
                              <div className="value-with-edit">
                                {/* точка дила живёт в строке AI, когда та включена */}
                                {!aiMergedOn && renderDealDot(cwDeals, setInfoPopup)}
                                {cwForecast}
                                {cwChange && cwChange !== 'N/A' && (
                                  <span className={`change-indicator ${cwChange.startsWith('+') ? 'positive' : 'negative'}`}>
                                    {cwChange}
                                  </span>
                                )}
                              </div>
                            </td>
                          );
                        })()}

                        {/* Prediction Weeks - Forecast */}
                        {visibleColumns.prediction && forecastWeeks.map((week, idx) => {
                          const forecast = calculateForecastForProductConditional(
                            product,
                            week.weekNumber,
                            week.year,
                            avgGrowth,
                            activeMarketplace
                          );
                          const previous = getSalesData(identifier, week.year - 1, week.weekNumber);
                          const change = calculatePercentageChange(forecast, previous);
                          const fwRange = isoWeekRangeStr(week.year, week.weekNumber);
                          const fwDeals = dealsForRange(dealsData[identifier], fwRange.start, fwRange.end);
                          // Forecast cell color
                          const cellClass = getForecastCellClassForMarketplace(identifier, week.weekNumber, week.year, activeMarketplace, product);
                          return (
                            <React.Fragment key={idx}>
                              <td className={`prediction-cell ${cellClass}`}>
                                <div className="current-year-value">
                                  {!aiMergedOn && renderDealDot(fwDeals, setInfoPopup)}
                                  {forecast}
                                  {change && change !== 'N/A' && (
                                    <span className={`change-indicator ${change.startsWith('+') ? 'positive' : 'negative'}`}>
                                      {change}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>

                      {/* ── Agentic (AI Forecast) Row ──────────────── */}
                      {showAgenticRow && activeMarketplace === 'amazon' && currentWeekInfo && (() => {
                        const wkKey = (y, w) => `${y}-W${String(w).padStart(2, '0')}`;
                        const aiData = aiForecastData.week?.ai?.[identifier] || {};
                        // Days Supply / Due Date по прогнозу агента — та же
                        // математика, что у колонок страницы, но потребление
                        // стока идёт по прогнозу агента; выводится в ТЕ ЖЕ
                        // колонки (mp days / total days / due date / days+plan)
                        const hasAi = Object.keys(aiData).length > 0;
                        // значения агента — объекты { value, p10, p50, p90, accuracy }
                        const aiForecastFn = (id, w, y) => {
                          const v = aiData[wkKey(y, w)]?.value;
                          return typeof v === 'number' ? v : 'N/A';
                        };
                        const aiFba = Number(product.fbaStock) || 0;
                        const aiAwd = Number(product.awdStock) || 0;
                        const aiWh = Number(product.whStock) || 0;
                        // план агента (рекомендация); без неё дни считаем по нашему плану
                        const aiPlanRec = aiPlanData[identifier];
                        const aiPlanQty = aiPlanRec != null ? aiPlanRec : (planToProduceData[product.sku] || 0);
                        const aiCalcDays = (stock) => calculateDaysSupplied(
                          stock, identifier, null, null, currentWeekInfo, () => 0, aiForecastFn);
                        const aiDaysMp = hasAi ? aiCalcDays(aiFba + aiAwd) : null;
                        const aiDaysTotal = hasAi ? aiCalcDays(aiFba + aiAwd + aiWh) : null;
                        const aiDaysPlan = hasAi ? aiCalcDays(aiFba + aiAwd + aiWh + aiPlanQty) : null;
                        return (
                          <tr className="agentic-row">
                            {/* одинаковые с нашей строкой ячейки (чекбокс, имя,
                                ASIN, стоки...) накрыты rowSpan сверху; строка
                                начинается со своих 5 колонок — mp days, total
                                days, due date, plan, days+plan по данным агента */}
                            <td className="agentic-cell" title="Days of FBA+AWD stock by the AI forecast">
                              {aiDaysMp !== null ? renderDaysSuppliedCell(aiDaysMp) : '—'}
                            </td>
                            <td className="agentic-cell" title="Days of total stock (WH+FBA+AWD) by the AI forecast">
                              {aiDaysTotal !== null ? renderDaysSuppliedCell(aiDaysTotal) : '—'}
                            </td>
                            <td className="agentic-cell" title="Due date by the AI forecast">
                              {aiDaysMp !== null ? (
                                <div className={`due-date-value ${getDueDateClass(aiDaysMp)}`}>
                                  {calculateDueDate(aiDaysMp)}
                                </div>
                              ) : '—'}
                            </td>
                            <td className="agentic-cell" title="AI recommended Plan to Produce">
                              {aiPlanRec != null ? aiPlanRec.toLocaleString() : '—'}
                            </td>
                            <td className="agentic-cell" title="Days of stock incl. AI recommended plan, by the AI forecast">
                              {aiDaysPlan !== null ? renderDaysSuppliedCell(aiDaysPlan) : '—'}
                            </td>

                            {/* Historical cells отсутствуют: их накрывает rowSpan=2
                                объединённой ячейки основной строки (сетка
                                факт/наш прогноз/ИИ с точностью) */}

                            {/* Current week: значение + прирост к прошлому году +
                                точность + «?» (тултип + попап по клику) */}
                            {visibleColumns.currentWeek && (() => {
                              const r = isoWeekRangeStr(currentWeekInfo.year, currentWeekInfo.weekNumber);
                              return (
                                <td className="agentic-cell current-week-cell">
                                  {renderDealDot(dealsForRange(dealsData[identifier], r.start, r.end), setInfoPopup)}
                                  {renderAiValue(
                                    aiData[wkKey(currentWeekInfo.year, currentWeekInfo.weekNumber)],
                                    getSalesData(identifier, currentWeekInfo.year - 1, currentWeekInfo.weekNumber),
                                    setInfoPopup
                                  )}
                                </td>
                              );
                            })()}

                            {/* Forecast weeks */}
                            {visibleColumns.prediction && forecastWeeks.map((week, idx) => {
                              const r = isoWeekRangeStr(week.year, week.weekNumber);
                              return (
                                <td key={idx} className="agentic-cell prediction-cell">
                                  {renderDealDot(dealsForRange(dealsData[identifier], r.start, r.end), setInfoPopup)}
                                  {renderAiValue(
                                    aiData[wkKey(week.year, week.weekNumber)],
                                    getSalesData(identifier, week.year - 1, week.weekNumber),
                                    setInfoPopup
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })()}

                      {/* ── Stock Balance Row ──────────────────────── */}
                      {showStockBalanceRow && currentWeekInfo && (() => {
                        const stockBalances = calculateWeeklyStockBalances(product, avgGrowth);
                        const currentKey = `${currentWeekInfo.year}-W${currentWeekInfo.weekNumber}`;
                        // count fixed left columns to span
                        const fixedColCount =
                          1 + // checkbox
                          1 + // product name
                          1 + // identifier
                          1 + // bags
                          1 + // wh stock
                          marketplaceConfig.stockColumns.length +
                          1 + // ship
                          (marketplaceConfig.daysSuppliedKey ? 1 : 0) +
                          1 + // total days
                          1 + // due date
                          1 + // plan to produce
                          1 + // days + plan
                          0; // monthly plan columns removed from weekly view

                        // Calculate how many forecast weeks the given endBalance can cover,
                        // starting from the week AFTER the current cell's week.
                        // Also returns a breakdown string for the tooltip.
                        const calcWeeksOfStock = (endBalance, fromWeekKey) => {
                          if (endBalance <= 0) return { weeks: 0, lines: [] };
                          let remaining = endBalance;
                          let weeks = 0;
                          const lines = [];
                          // Find starting index — skip weeks up to and including fromWeekKey
                          let startIdx = 0;
                          if (fromWeekKey) {
                            const idx = forecastWeeks.findIndex(
                              fw => `${fw.year}-W${fw.weekNumber}` === fromWeekKey
                            );
                            // For current week key (not in forecastWeeks) idx === -1, start from 0
                            startIdx = idx >= 0 ? idx + 1 : 0;
                          }
                          for (let i = startIdx; i < forecastWeeks.length; i++) {
                            const fw = forecastWeeks[i];
                            const fwKey = `${fw.year}-W${fw.weekNumber}`;
                            const fwEntry = stockBalances[fwKey];
                            const fwForecast = fwEntry ? fwEntry.forecast : 0;
                            if (fwForecast <= 0) {
                              weeks++;
                              lines.push(`W${fw.weekNumber}: forecast 0, carry ${remaining.toLocaleString()}`);
                              continue;
                            }
                            if (remaining >= fwForecast) {
                              remaining -= fwForecast;
                              weeks++;
                              lines.push(`W${fw.weekNumber}: −${fwForecast.toLocaleString()} → ${remaining.toLocaleString()} left`);
                            } else {
                              // Partial week
                              const partial = (remaining / fwForecast * 7).toFixed(1);
                              lines.push(`W${fw.weekNumber}: partial (${remaining.toLocaleString()}/${fwForecast.toLocaleString()}, ~${partial}d)`);
                              weeks = weeks + remaining / fwForecast;
                              remaining = 0;
                              break;
                            }
                          }
                          return { weeks: Math.round(weeks * 10) / 10, lines };
                        };

                        const renderBalanceCell = (entry, extraClass = '', weekKey = '', colSpan = 1) => {
                          if (!entry) return <td colSpan={colSpan} className={`stock-balance-cell stock-balance-empty ${extraClass}`}>—</td>;
                          const { endBalance, tooltip, startBalance, production, forecast } = entry;
                          const cls = endBalance < 0 ? 'balance-negative' : endBalance === 0 ? 'balance-zero' : 'balance-positive';
                          const { weeks: weeksOfStock, lines } = calcWeeksOfStock(endBalance, weekKey);
                          const coverageLines = lines.length > 0
                            ? '\n\nCoverage breakdown:\n' + lines.join('\n')
                            : '';
                          const fullTooltip = tooltip + coverageLines;
                          return (
                            <td
                              colSpan={colSpan}
                              className={`stock-balance-cell ${extraClass} ${cls}`}
                              title={fullTooltip}
                            >
                              {endBalance.toLocaleString()}
                              <span className="stock-weeks-suffix" style={{ opacity: 0.65, fontSize: '0.85em', marginLeft: '3px' }}>
                                /{weeksOfStock}w
                              </span>
                            </td>
                          );
                        };

                        return (
                          <tr className="stock-balance-row">
                            {/* первые 2 ячейки sticky — та же защита от
                                горизонтального скролла, что у строки AI */}
                            <td className="checkbox-fixed-column stock-balance-label-cell" />
                            <td className="yoy-fixed-column stock-balance-label-cell">
                              <span className="stock-balance-label">Est. End-of-Week Stock</span>
                            </td>
                            {fixedColCount - 2 > 0 && (
                              <td colSpan={fixedColCount - 2} className="stock-balance-label-cell" />
                            )}

                            {/* Historical weeks — empty cells to keep alignment */}
                            {visibleColumns.historical && historicalWeeks.map((_, idx) => (
                              <td key={idx} className="stock-balance-cell stock-balance-empty" />
                            ))}

                            {/* Current week */}
                            {visibleColumns.currentWeek && renderBalanceCell(stockBalances[currentKey], 'current-week-cell', currentKey, 1)}

                            {/* Forecast weeks */}
                            {visibleColumns.prediction && forecastWeeks.map((week, idx) => {
                              const key = `${week.year}-W${week.weekNumber}`;
                              return React.cloneElement(
                                renderBalanceCell(stockBalances[key], 'prediction-cell', key, 1),
                                { key: idx }
                              );
                            })}
                          </tr>
                        );
                      })()}

                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── SUMMARY TABLE (Weekly only) ─────────────────────── */}
        {!loadingProducts && filteredProducts.length > 0 && viewMode === 'weekly' && activeMarketplace === 'summary' && (
          <YoySummaryTable
            filteredProducts={filteredProducts}
            identifierField={identifierField}
            currentWeekInfo={currentWeekInfo}
            historicalWeeks={historicalWeeks}
            forecastWeeks={forecastWeeks}
            historicalWeeksCount={historicalWeeksCount}
            forecastWeeksCount={forecastWeeksCount}
            visibleColumns={visibleColumns}
            salesDataByMarketplace={salesDataByMarketplace}
            weeklyPlanData={weeklyPlanData}
            planToProduceData={planToProduceData}
            showStockBalanceRow={showStockBalanceRow}
            calculateForecastForProductConditional={calculateForecastForProductConditional}
            calculateAverageGrowthForProduct={calculateAverageGrowthForProduct}
          />
        )}

        {/* ── MONTHLY VIEW ─────────────────────────────────────────── */}
        {!loadingProducts && filteredProducts.length > 0 && viewMode === 'monthly' && activeMarketplace !== 'summary' && (
          <YoyMonthlyView
            filteredProducts={filteredProducts}
            productsData={productsData}
            identifierField={identifierField}
            marketplaceConfig={marketplaceConfig}
            activeMarketplace={activeMarketplace}

            salesDataByMarketplace={monthlySalesDataByMarketplace}

            visibleColumns={visibleColumns}
            showAllPreviousYears={showAllPreviousYears}
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}

            historicalMonthsCount={historicalMonthsCount}
            forecastMonthsCount={forecastMonthsCount}

            seasonalityFlags={seasonalityFlags}
            getSeasonalityCoefficient={getSeasonalityCoefficient}
            toggleSeasonalityFlag={toggleSeasonalityFlag}

            activeDaysSuppliedData={activeDaysSuppliedData}
            planToProduceData={planToProduceData}
            monthlyPlanData={monthlyPlanData}
            monthlyColumnsExpanded={monthlyColumnsExpanded}
            setMonthlyColumnsExpanded={setMonthlyColumnsExpanded}
            monthlyDisplayMonths={monthlyDisplayMonths}

            calculateBoxCanBeShipped={calculateBoxCanBeShipped}
            calculateMonthlyDaysSupplied={calculateMonthlyDaysSupplied}
            calculateDaysSupplyWithCumulativePlan={calculateDaysSupplyWithCumulativePlan}
            calculateDaysSupplyWithTotalPlan={calculateDaysSupplyWithTotalPlan}
            getMonthlyPlanTooltip={getMonthlyPlanTooltip}
            getMarketplaceDaysData={getMarketplaceDaysData}
            getTotalStockDaysData={getTotalStockDaysData}
            getTotalPlanDaysData={getTotalPlanDaysData}
            openEditPopup={openEditPopup}
            showStockBalanceRow={showStockBalanceRow}
            showAgenticRow={showAgenticRow}
            aiForecastData={aiForecastData.month}
            dealsData={dealsData}
            aiPlanData={aiPlanData}
            onShowInfo={setInfoPopup}
          />
        )}


        {/* ── SUMMARY TABLE (Monthly) */}
        {!loadingProducts && filteredProducts.length > 0 && viewMode === 'monthly' && activeMarketplace === 'summary' && (
          <YoySummaryMonthlyTable
            filteredProducts={filteredProducts}
            identifierField={identifierField}
            monthlySalesDataByMarketplace={monthlySalesDataByMarketplace}
            monthlyPlanData={monthlyPlanData}
            planToProduceData={planToProduceData}
            visibleColumns={visibleColumns}
            historicalMonthsCount={historicalMonthsCount}
            forecastMonthsCount={forecastMonthsCount}
            showStockBalanceRow={showStockBalanceRow}
            seasonalityFlags={seasonalityFlags}
            getSeasonalityCoefficient={getSeasonalityCoefficient}
          />
        )}
        {!loadingProducts && filteredProducts.length === 0 && searchQuery && (
          <div className="no-results">No products found for "{searchQuery}"</div>
        )}

        {!loadingProducts && productsData.length === 0 && (
          <div className="no-results">No products found in database</div>
        )}
      </div>

      {/* Edit Popup */}
      <YoyEditPopup
        editPopup={editPopup}
        loadingEdit={loadingEdit}
        onClose={closeEditPopup}
        onSave={handleSaveEdit}
        onEditPopupChange={setEditPopup}
        medianWeeksCount={medianWeeksCount}
        activeSalesData={activeSalesData}
        periodDeals={(() => {
          // дилы Amazon, пересекающиеся с периодом открытой ячейки
          // (только на вкладке Amazon — дилы амазоновские)
          if (!editPopup.isOpen || !editPopup.year) return [];
          if (editPopup.marketplace && editPopup.marketplace !== 'amazon') return [];
          const list = dealsData[editPopup.product?.asin];
          if (!list) return [];
          if (editPopup.mode === 'monthly') {
            if (!editPopup.month) return [];
            const mm = String(editPopup.month).padStart(2, '0');
            const last = new Date(Date.UTC(editPopup.year, editPopup.month, 0)).getUTCDate();
            return dealsForRange(list, `${editPopup.year}-${mm}-01`, `${editPopup.year}-${mm}-${String(last).padStart(2, '0')}`);
          }
          if (!editPopup.week) return [];
          const r = isoWeekRangeStr(editPopup.year, editPopup.week);
          return dealsForRange(list, r.start, r.end);
        })()}
        forecastHistory={(() => {
          // история прогнозов кликнутой исторической ячейки с подписями —
          // менеджеры забывают, что значат цифры объединённой ячейки
          if (!editPopup.isOpen || editPopup.field !== 'historical') return null;
          if (editPopup.marketplace && editPopup.marketplace !== 'amazon') return null;
          const asin = editPopup.product?.asin;
          if (!asin || !editPopup.year) return null;
          const isM = editPopup.mode === 'monthly';
          const data = isM ? aiForecastData.month : aiForecastData.week;
          if (!data) return null;
          const key = isM
            ? `${editPopup.year}-${String(editPopup.month).padStart(2, '0')}`
            : aiWeekKey(editPopup.year, editPopup.week);
          const page = data.page?.[asin]?.[key];
          const ai = data.ai?.[asin]?.[key];
          if (typeof page !== 'number' && typeof ai?.value !== 'number') return null;
          const actual = editPopup.currentValue === '' ? null : Number(editPopup.currentValue);
          return {
            actual,
            page: typeof page === 'number' ? page : null,
            pageAcc: forecastMatchPct(page, actual),
            ai: typeof ai?.value === 'number' ? ai.value : null,
            aiAcc: forecastMatchPct(ai?.value, actual),
          };
        })()}
      />

      {/* История версий ИИ-прогноза + откат */}
      {aiHistory.open && (
        <div className="popup-overlay" onClick={() => setAiHistory(h => ({ ...h, open: false }))}>
          <div className="popup-content popup-content--wide" onClick={e => e.stopPropagation()}>
            <div className="popup-header">
              <h2>AI Forecast History</h2>
              <button className="close-popup" onClick={() => setAiHistory(h => ({ ...h, open: false }))}>&#10005;</button>
            </div>
            <div className="edit-form">
              <div className="ai-history-tabs">
                {['week', 'month'].map(p => (
                  <button
                    key={p}
                    type="button"
                    className={`ai-history-tab${aiHistory.period === p ? ' is-active' : ''}`}
                    onClick={() => loadAiVersions(p)}
                  >
                    {p === 'week' ? 'Weekly' : 'Monthly'}
                  </button>
                ))}
              </div>
              {aiHistory.loading ? (
                <p className="median-loading">Loading…</p>
              ) : aiHistory.versions.length === 0 ? (
                <p className="median-empty">No agent submissions yet.</p>
              ) : (
                <table className="deals-table">
                  <thead>
                    <tr>
                      <th>Submitted</th>
                      <th>Products</th>
                      <th>Periods</th>
                      <th>Values</th>
                      <th>By</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {aiHistory.versions.map((v, i) => (
                      <tr key={v.version}>
                        <td className="deals-table-type">{v.version}</td>
                        <td>{v.products}</td>
                        <td>{v.periods}</td>
                        <td>{v.rows}</td>
                        <td>{v.bot || '—'}</td>
                        <td>
                          {i === 0 ? (
                            <span className="deal-pill deal-pill--completed">current</span>
                          ) : (
                            <button
                              type="button"
                              className="ai-history-revert"
                              disabled={aiHistory.busy}
                              onClick={() => rollbackAiForecast(v.version)}
                            >
                              Revert to this
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="flag-hint">
                Every agent submission is kept as a version (nothing is ever overwritten).
                Revert writes a NEW version copying that state for current and future periods;
                past periods stay frozen for the honest accuracy comparison.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Мини-попап деталей (клик по «?» строки AI или кружочку дила) */}
      {infoPopup && (
        <div className="popup-overlay" onClick={() => setInfoPopup(null)}>
          <div className="popup-content info-popup" onClick={e => e.stopPropagation()}>
            <div className="popup-header">
              <h2>{infoPopup.title}</h2>
              <button className="close-popup" onClick={() => setInfoPopup(null)}>&#10005;</button>
            </div>
            {infoPopup.rows && (
              <div className="info-rows">
                {infoPopup.rows.map((r, i) => (
                  <div key={i} className={`info-row${r.accent ? ' info-row--accent' : ''}`}>
                    <span className="info-row-label">{r.label}</span>
                    <span className="info-row-value">{r.value}</span>
                  </div>
                ))}
              </div>
            )}
            {infoPopup.deals && (
              <div className="info-deals">
                {infoPopup.deals.map((d, i) => (
                  <div key={i} className="deal-card">
                    <div className="deal-card-head">
                      <span className="deal-card-type">{d.type || 'Deal'}</span>
                      <span className={`deal-pill deal-pill--${d.status}`}>{d.status}</span>
                    </div>
                    <div className="deal-card-dates">
                      {fmtDealDate(d.start_date)} – {fmtDealDate(d.end_date)}, {d.end_date.slice(0, 4)}
                    </div>
                    {d.units_sold != null && (
                      <div className="deal-card-units">
                        <strong>{d.units_sold.toLocaleString()}</strong> units sold during the deal
                      </div>
                    )}
                    {d.note && <div className="deal-card-note">{d.note}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stock Upload Popup - only for Amazon */}
      {activeMarketplace === 'amazon' && (
        <YoyAmzStockPopup
          showStockUploadPopup={showStockUploadPopup}
          setShowStockUploadPopup={setShowStockUploadPopup}
          selectedStockFile={selectedStockFile}
          setSelectedStockFile={setSelectedStockFile}
          parsedStockData={parsedStockData}
          setParsedStockData={setParsedStockData}
          uploadStockStatus={uploadStockStatus}
          setUploadStockStatus={setUploadStockStatus}
          stockFileInputRef={stockFileInputRef}
          handleStockFileUpload={handleStockFileUpload}
          handleStockUploadSubmit={handleStockUploadSubmit}
          onSyncSuccess={loadProducts}
        />
      )}
    </>
  );
}

export default YOYForecastPage;